import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useAppStore } from '@core/store'
import { estiloDoElemento } from '@viewer/section-box/estilosDeCorte'
import { leitorDeMateriais } from '@viewer/section-box/materiaisDoModelo'
import {
  apanhaLentePara,
  lentesDasRegras,
  nomesDosElementos,
  precisaDeNomes,
} from '@viewer/section-box/lentesNoCorte'
import { ouvirLentes } from '@viewer/lens/lensRules'
import type { EstiloDoCorteNoPapel } from './lib/tramaNoPapel'
import { useIsMobile } from '@core/useIsMobile'
import ToastHost from '@core/layout/ToastHost'
import { viewerApi } from '@viewer/lib/viewerApi'
import { downloadBlob } from '@core/lib/downloadBlob'
import { boxCentre, modelWorldBox, type WorldBox } from './modelBox'
import {
  drawingAtPaperScale,
  drawingToDxf,
  drawingToPdf,
  drawingToSvg,
  dxfToBytes,
  pdfLayout,
  pdfOverflowMessage,
  planViewDef,
  replanViewDef,
  sectionViewDef,
  viewBasis,
  addAnnotation,
  makeAnnotation,
  moveAnnotation,
  newAnnotationId,
  removeAnnotation,
  setAnnotationText,
  addDimension,
  dimensionLabel,
  makeDimension,
  newDimensionId,
  removeDimension,
  buildSnapIndex,
  orthoPoint,
  resolveSnap,
  snapRadiusDrawing,
  stripArcs,
  resolveLineWeights,
  sanitizeProjWeight,
  PROJ_WEIGHT_CHOICES,
  PROJ_WEIGHT_LABELS,
  type ProjWeightChoice,
  buildPickIndex,
  chainPolylines,
  elementMeasure,
  nearestTarget,
  type PickTarget,
  alignAngleDeg,
  rotateViewDef,
  PLAN_DEFAULT_CUT_OFFSET,
  PLAN_DEFAULT_DEPTH,
  type Annotation2D,
  type Dimension2D,
  type Drawing2D,
  type Point2,
  type SnapPoint,
  type Vec3,
  type View2DDef,
  type View2DModelSource,
} from './lib'
import {
  fmtPt,
  measure,
  measureSummary,
  polygonCentroid,
  type MeasureResult,
} from './measure'
import {
  drawingPolylineCount,
  filterDrawingLayers,
  generateViewForModel,
} from './generateForViewer'
import {
  applyLayerStyles,
  defaultFill,
  defaultStroke,
  resetLayerStyle,
  setLayerStyle,
  type LayerStyles,
} from './layerStyles'
import { pickLocalIdAt, type PickNode } from './pickLocalId'
import { collectStoreys, planDepthFor, type Storey2D } from './storeys'
import { declaredStoreyContext } from './storeyContext'
import { sectionViewFromBox } from './sectionFromBox'
import {
  flipSectionView,
  nextSectionMarkName,
  sectionViewFromLine,
  DRAWN_SECTION_DEFAULT_DEPTH,
} from './sectionFromLine'
import {
  clientToSvg,
  drawingPointToContainerPx,
  drawingPointToWorld,
  svgMetrics,
  worldPointToDrawing,
} from './drawingSvgMap'
import { goToView3D } from './goTo3D'
import {
  duplicateView,
  loadSavedViews,
  loadSnapPref,
  newViewId,
  persistSavedViews,
  removeView,
  renameView,
  sanitizeFileName,
  saveSnapPref,
  uniqueViewName,
  upsertView,
} from './views2dStorage'
import type { PaperName, SavedView2D, ScaleChoice } from './views2dTypes'
import {
  strokeUnitsForPx,
  useSvgPanZoom,
  PREVIEW_CUT_PX,
  PREVIEW_PROJ_PX,
  PX_PER_MM,
} from './useSvgPanZoom'
import { useDetachedWindow } from '@core/layout/janela-destacada/useDetachedWindow'
import {
  applyCropDrag,
  cropHandleCursor,
  cropHandlePoints,
  cropIsUsable,
  normalizeCrop,
  type CropHandle,
} from './lib/cropEdit'
import ViewNavigator from './ViewNavigator'
import SurfaceSwitch from './SurfaceSwitch'
import {
  AnnotGlyph,
  DimGlyph,
  MarkGlyph,
  MeasureGlyph,
  PickGlyph,
  SnapGlyph,
} from './glyphs'
import { NumberField } from './parts'
import { fmtNum, scaleLabel } from './format'
import { mobileViewOptions } from './mobileViewOptions'

// ---------------------------------------------------------------------------
// MODO 2D: a superfície de desenho da plataforma.
//
// Era uma janela flutuante ("Vistas 2D") e passou a ser um MODO: o
// comutador 3D | 2D troca o que ocupa a área central do viewer. O
// desenho fica com o ecrã todo, o navegador de vistas à esquerda, e o
// painel de propriedades da direita (que é da aplicação, não desta
// superfície) mantém-se montado: clicar numa parede no desenho mostra-a
// lá, que era o ganho principal de promover isto a modo.
//
// Três maneiras de criar uma vista:
//   - planta por piso (IfcBuildingStorey, cota lida do mundo)
//   - planta livre (cota de corte escrita à mão)
//   - corte: da caixa de corte, por azimute, ou DESENHADO sobre a
//     planta com dois cliques (fluxo clássico de desenho técnico)
//
// Modo local: não depende de backend nenhum. As vistas gravadas vivem no
// localStorage, indexadas pelo SHA-256 do IFC.
// ---------------------------------------------------------------------------

const SCALE_OPTIONS: ScaleChoice[] = [50, 100, 200, 500, 'fit']

/** Realce do elemento seleccionado na pré-visualização. Laranja: não
 *  colide com nenhuma das cores da paleta bSPT (azul, turquesa,
 *  vermelho, magenta) nem com os cinzentos das camadas. */
const SELECT_STROKE = '#f97316'
const SELECT_FILL = '#fed7aa'
/** Espessura do traço realçado, em pixéis de ecrã. */
const PREVIEW_SELECT_PX = 4
/** Realce do que está debaixo do rato no modo "Área do elemento" (azul
 *  claro: é escolha, ainda não é selecção). */
const PICK_HOVER_STROKE = '#38bdf8'
const PICK_HOVER_FILL = '#e0f2fe'
/** Quanto o rato pode andar entre o carregar e o largar para aquilo
 *  ainda contar como clique (e não como arrasto do desenho), em px. */
const CLICK_SLOP_PX = 4
/** Raio, em pixéis de ecrã, dentro do qual clicar no primeiro ponto
 *  FECHA o polígono da medição. */
const MEASURE_SNAP_PX = 12
/** Quanto o rato tem de andar para agarrar mesmo uma etiqueta, em px.
 *  Abaixo disto é um clique (ou um duplo clique, que abre a edição). */
const ANNOT_DRAG_SLOP_PX = 3
/** Raio do object snap, em pixéis de ECRÃ: é a distância a que a mão
 *  está, não a que o modelo está. Independente do zoom e da escala. */
const SNAP_PX = 10

/** Raio da pontaria dos modos de escolha, em pixéis de ECRÃ. Maior do
 *  que o do snap (10 px): ali aponta-se a um PONTO, aqui a uma linha
 *  inteira, e uma linha é um alvo grande. */
const PICK_PX = 8
/** Tolerância máxima, em metros do desenho, para duas pontas de troços
 *  escolhidos contarem como encostadas. O raio de ecrã da pontaria
 *  também vale, mas com o desenho enquadrado 8 px podem ser metros e
 *  fronteiras que não fecham fechavam à mesma. Dois centímetros é o
 *  tamanho da folga de construção que ainda se aceita. */
const PICK_JOIN_MAX_M = 0.02
/** Janela do duplo clique, em ms (o valor corrente dos sistemas). */
const DOUBLE_CLICK_MS = 400

/** Ferramenta activa sobre a pré-visualização. Só uma de cada vez: são
 *  todas gestos de clique sobre o mesmo desenho.
 *
 *  Três maneiras de medir, e não uma: 'measure' clica PONTOS (para o que
 *  não tem linha desenhada), 'pick' escolhe LINHAS já desenhadas (o
 *  "Pick Lines" dos CAD) e 'elem' mede a secção cortada de um ELEMENTO
 *  inteiro. */
type Tool2D =
  | 'none'
  | 'draw'
  | 'annot'
  | 'measure'
  | 'pick'
  | 'elem'
  // Dois cantos definem a janela de recorte; dois pontos definem a
  // direcção com que a vista fica alinhada. Ambas reutilizam a mecânica
  // de dois cliques do "desenhar corte", que já é a que o utilizador
  // conhece.
  | 'crop'
  | 'align'

/** Etiqueta curta de um corte gravado ("Corte A-A" -> "A-A"). */
function markLabel(name: string): string {
  return name.replace(/^Corte\s+/i, '')
}

interface DrawState {
  view: View2DDef
  drawing: Drawing2D
  modelId: string
}

function Views2DSurface() {
  const surface = useAppStore((s) => s.viewerSurface)
  const setSurface = useAppStore((s) => s.setViewerSurface)
  const models = useAppStore((s) => s.models)
  const activeModelId = useAppStore((s) => s.activeModelId)
  const pushToast = useAppStore((s) => s.pushToast)
  const sectionOrientation = useAppStore((s) => s.sectionOrientation)
  const sectionExtents = useAppStore((s) => s.sectionExtents)
  // Sincronização INVERSA (3D -> 2D): a selecção já vive no store e a
  // superfície já lê o store, por isso sai de graça: seleccionar no 3D
  // realça no desenho sem mais nada pelo meio.
  const selection = useAppStore((s) => s.selection)
  const clearSelection = useAppStore((s) => s.clearSelection)
  const isMobile = useIsMobile()

  // --- destacar para uma janela do browser ---------------------------------
  // O ganho é ter o modelo num ecrã e a planta no outro. A janela nasce do
  // mesmo contexto JS (ver `useDetachedWindow`), por isso a selecção
  // continua a ser a mesma nos dois lados sem nada pelo meio: é o store.
  const detached = useAppStore((s) => s.views2dDetached)
  const setDetached = useAppStore((s) => s.setViews2DDetached)

  const onWindowOpen = useCallback(() => {
    // Só agora se larga a área central para o 3D: se se fizesse no
    // clique, um popup bloqueado deixava o utilizador sem 2D nenhum.
    setSurface('3d')
  }, [setSurface])

  const onWindowBlocked = useCallback(() => {
    setDetached(false)
    pushToast(
      'O browser bloqueou a janela do 2D. Autoriza as janelas emergentes ' +
        'deste site (ícone à direita da barra de endereço) e tenta outra vez.',
      { type: 'error' },
    )
  }, [setDetached, pushToast])

  const onWindowClose = useCallback(() => {
    // Fechar a janela devolve o 2D à área central. `setViewerSurface('2d')`
    // limpa o `views2dDetached` sozinho (ver `views2dSlice`).
    setSurface('2d')
  }, [setSurface])

  const detachedHandlers = useMemo(
    () => ({
      onOpen: onWindowOpen,
      onBlocked: onWindowBlocked,
      onClose: onWindowClose,
    }),
    [onWindowOpen, onWindowBlocked, onWindowClose],
  )
  const { win: detachedWin, container: detachedHost } = useDetachedWindow(
    detached,
    detachedHandlers,
  )

  /** A janela onde o desenho está a ser visto. Teclado e caixas de
   *  diálogo são POR JANELA: com o 2D destacado, ouvir o teclado da
   *  principal (ou abrir um `prompt` lá) era ouvir e falar no ecrã
   *  errado. */
  const hostWin: Window | null =
    detached && detachedWin
      ? detachedWin
      : typeof window === 'undefined'
        ? null
        : window

  /** No modo 3D esta superfície não se desenha, mas MANTÉM-SE montada
   *  (devolve null): o desenho gerado, a vista aberta e as anotações são
   *  estado deste componente, e voltar ao 2D tem de reabrir onde se
   *  estava, sem regerar nada. Destacada, está activa com a área central
   *  em 3D: é esse o objectivo. */
  const active = surface === '2d' || detached
  /** Layout de telemóvel: nunca com a janela destacada (o botão de
   *  destacar não existe em mobile, mas encolher a principal depois de
   *  destacar não pode partir a janela externa). */
  const mobileLayout = isMobile && !detached

  const modelId = activeModelId ?? models[0]?.id ?? null
  // As tramas do corte são as do 3D (base e regras por classe ou
  // material): o que se vê cortado no 3D é o que sai na planta. A camada
  // de cada polilinha já é a classe IFC.
  const estiloBaseDeCorte = useAppStore((s) => s.estiloBaseDeCorte)
  const regrasDeCorte = useAppStore((s) => s.regrasDeCorte)
  const [versaoDasLentes, setVersaoDasLentes] = useState(0)
  useEffect(() => ouvirLentes(() => setVersaoDasLentes((v) => (v + 1) & 0xffff)), [])
  const lentesDoCorte = useMemo(
    () => lentesDasRegras(regrasDeCorte.filter((r) => r.activa && r.valor.trim())),
    // A versão muda quando se grava uma lente: relê-las.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [regrasDeCorte, versaoDasLentes],
  )
  // Os nomes dos elementos cortados, só quando uma lente olha para eles.
  const [nomesDoCorte, setNomesDoCorte] = useState<ReadonlyMap<number, string | null>>(new Map())
  const modelEntry = models.find((m) => m.id === modelId) ?? null
  const sha256 = modelEntry?.sha256 ?? ''

  // --- estado --------------------------------------------------------------
  const [storeys, setStoreys] = useState<Storey2D[]>([])
  const [storeysBusy, setStoreysBusy] = useState(false)
  const [current, setCurrent] = useState<DrawState | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [cutOffset, setCutOffset] = useState(PLAN_DEFAULT_CUT_OFFSET)
  /** Altura de corte das plantas POR PISO. Separada do campo da barra,
   *  que a planta livre põe a 0 (a cota escrita já é a do corte): sem
   *  esta separação, a planta por piso seguinte cortava rente ao
   *  pavimento. Só muda quando se regenera uma planta por piso. */
  const [storeyCutOffset, setStoreyCutOffset] = useState(
    PLAN_DEFAULT_CUT_OFFSET,
  )
  /** Ids das plantas livres (as que têm a cota de corte escrita à mão). */
  const freePlanIds = useRef(new Set<string>())
  const [depth, setDepth] = useState(PLAN_DEFAULT_DEPTH)
  const [scale, setScale] = useState<ScaleChoice>(100)
  const [paper, setPaper] = useState<PaperName>('A4')
  const [landscape, setLandscape] = useState(true)
  const [hiddenLayers, setHiddenLayers] = useState<Set<string>>(new Set())
  const [saved, setSaved] = useState<SavedView2D[]>([])
  const [showLayers, setShowLayers] = useState(false)
  const [showMarks, setShowMarks] = useState(true)
  /** Cores escolhidas pelo utilizador, por camada. Vazio = paleta. */
  const [layerStyles, setLayerStyles] = useState<LayerStyles>({})
  /** Poché: preencher os contornos fechados do corte. */
  const [fillCuts, setFillCuts] = useState(true)
  /** Arcos suaves: desenhar as curvas reconhecidas como arcos em vez da
   *  tesselação. Desligar é barato (os `points` nunca deixaram de lá
   *  estar), por isso não obriga a regerar o desenho. */
  const [smoothArcs, setSmoothArcs] = useState(true)
  /** Peso e contraste do traço de projecção. 'auto' adapta-se ao
   *  desenho (ver `lineWeights.ts`); é só o valor de partida e o
   *  utilizador manda sempre por cima. Não obriga a regerar: é
   *  apresentação. */
  const [projWeight, setProjWeight] = useState<ProjWeightChoice>('auto')
  /** Remoção de linhas ocultas. Ao contrário das anteriores, ESTA muda o
   *  desenho e obriga a gerar outra vez. */
  const [hideHidden, setHideHidden] = useState(false)

  // Planta livre
  const [freeElevation, setFreeElevation] = useState(1.2)
  const [freeDepth, setFreeDepth] = useState(PLAN_DEFAULT_DEPTH)

  // Corte manual por azimute
  const [manualName, setManualName] = useState('Corte')
  const [manualAzimuth, setManualAzimuth] = useState(0)
  const [manualDepth, setManualDepth] = useState(20)
  const [manualOrigin, setManualOrigin] = useState<Vec3>([0, 0, 0])
  const [originSeeded, setOriginSeeded] = useState<string | null>(null)
  /** Extensão robusta do modelo (ver `modelBox.ts`): dá a altura aos
   *  cortes desenhados e a origem por omissão do corte manual. */
  const [worldBox, setWorldBox] = useState<WorldBox | null>(null)

  // Ferramentas sobre o desenho (desenhar corte, anotar, medir). No
  // telemóvel não há ferramentas: o desenho é para ver, com pan e zoom.
  const [tool, setTool] = useState<Tool2D>('none')
  const drawMode = !mobileLayout && tool === 'draw'
  const annotMode = !mobileLayout && tool === 'annot'
  const measureMode = !mobileLayout && tool === 'measure'
  const pickMode = !mobileLayout && tool === 'pick'
  /** "Área do elemento" NÃO é uma ferramenta do overlay: é a selecção de
   *  sempre com uma conta por cima. Fica com o clique da
   *  pré-visualização (que já sabe apanhar traços finos e manchas de
   *  poché por amostragem) e com o pan por arrasto. */
  const elemMode = !mobileLayout && tool === 'elem'
  const cropMode = !mobileLayout && tool === 'crop'
  const alignMode = !mobileLayout && tool === 'align'
  /** Com uma ferramenta ligada é o overlay que recebe os cliques: o
   *  pan/zoom por arrasto e a selecção de elementos ficam suspensos (a
   *  roda do rato continua a dar zoom). */
  const toolActive =
    drawMode || annotMode || measureMode || pickMode || cropMode || alignMode
  /** Object snap: preferência de quem desenha, não da vista. Ligado por
   *  omissão. `snapHit` é a marca que está a ser mostrada. */
  const [snapOn, setSnapOn] = useState(() => loadSnapPref())
  const [snapHit, setSnapHit] = useState<SnapPoint | null>(null)
  const [drawDepth, setDrawDepth] = useState(DRAWN_SECTION_DEFAULT_DEPTH)
  const [drawP1, setDrawP1] = useState<Point2 | null>(null)
  const [drawCursor, setDrawCursor] = useState<Point2 | null>(null)
  /** Recorte a ser ajustado por uma pega. Enquanto existe, é ele que se
   *  desenha; ao largar, é gravado. */
  const [cropPreview, setCropPreview] = useState<{
    minX: number
    minY: number
    maxX: number
    maxY: number
  } | null>(null)
  /** Arrasto em curso de uma pega do recorte.
   *
   *  Guarda a caixa e o ponto de partida, e não o frame anterior: cada
   *  movimento recalcula-se a partir do início, para a caixa não fugir
   *  do cursor por acumulação de erro. Numa ref e não em estado, para
   *  os tratadores de ponteiro a lerem sempre actualizada e não pela
   *  closure do render em que nasceram. */
  const cropDragRef = useRef<{
    handle: CropHandle
    from: Point2
    inicial: { minX: number; minY: number; maxX: number; maxY: number }
  } | null>(null)

  // Anotações da vista actual
  const [annotations, setAnnotations] = useState<Annotation2D[]>([])
  const [selectedAnnot, setSelectedAnnot] = useState<string | null>(null)
  /** Caixa de texto inline: `id` a null = etiqueta nova. */
  const [editing, setEditing] = useState<{
    id: string | null
    at: Point2
    text: string
  } | null>(null)
  /** Arrasto de etiqueta em curso. `x0/y0` é onde o rato carregou (em
   *  pixéis de cliente) e `captured` diz se já se agarrou o ponteiro: o
   *  arrasto só começa depois de andar `ANNOT_DRAG_SLOP_PX`, senão um
   *  clique parado contava como arrasto de zero e, pior, a captura do
   *  ponteiro re-endereçava o `dblclick` para o overlay e a etiqueta
   *  nunca se conseguia editar. */
  const annotDragRef = useRef<{
    id: string
    dx: number
    dy: number
    x0: number
    y0: number
    captured: boolean
  } | null>(null)

  // Medição em curso (efémera) e cotas já terminadas (persistentes).
  // Terminar uma medição não devolve um número que se perde: deixa uma
  // COTA desenhada, como no 3D.
  const [measurePts, setMeasurePts] = useState<Point2[]>([])
  const [measureCursor, setMeasureCursor] = useState<Point2 | null>(null)
  const [dimensions, setDimensions] = useState<Dimension2D[]>([])
  const [selectedDim, setSelectedDim] = useState<string | null>(null)

  /** Modo "Escolher linhas": as CHAVES das polilinhas escolhidas, pela
   *  ordem dos cliques. Chaves e não objectos: o desenho é regerado
   *  (mudar de camada, ligar o poché) e um objecto agarrado ficava a
   *  apontar para geometria que já não está no ecrã. */
  const [pickedKeys, setPickedKeys] = useState<string[]>([])
  /** Chave da polilinha debaixo do cursor (realce do que é escolhível). */
  const [hoverKey, setHoverKey] = useState<string | null>(null)
  /** Modo "Área do elemento": o elemento medido. */
  const [elemPick, setElemPick] = useState<number | null>(null)
  /** Último troço escolhido e quando: é o que deixa o duplo clique
   *  terminar sem largar o que o primeiro clique escolheu. */
  const lastPickRef = useRef<{ key: string; t: number } | null>(null)

  /** Cota do piso por id de vista: a `View2DDef` de uma planta já traz o
   *  offset somado à origem, e sem isto não se podia mexer no offset
   *  depois de gerar. */
  const planElevations = useRef(new Map<string, number>())

  // Recarregar as vistas gravadas quando muda o modelo. Adopção durante
  // o render, não efeito (padrão da casa).
  const [prevSha, setPrevSha] = useState<string | null>(null)
  if (prevSha !== sha256) {
    setPrevSha(sha256)
    setSaved(sha256 ? loadSavedViews(sha256) : [])
    setCurrent(null)
    setHiddenLayers(new Set())
    setLayerStyles({})
    setTool('none')
    setDrawP1(null)
    setAnnotations([])
    setEditing(null)
    setSelectedAnnot(null)
    setMeasurePts([])
    setDimensions([])
    setSelectedDim(null)
    setPickedKeys([])
    setHoverKey(null)
    setElemPick(null)
  }

  /** Elemento realçado no desenho: é a selecção do viewer, desde que
   *  seja deste modelo. */
  const selectedLocalId =
    selection && modelId && selection.modelId === modelId
      ? selection.localId
      : null

  const getModel = useCallback((): View2DModelSource | null => {
    if (!modelId) return null
    return (viewerApi.getModel(modelId) ?? null) as View2DModelSource | null
  }, [modelId])

  // --- pisos ---------------------------------------------------------------
  // Nota: o primeiro `await` é deliberado. A leitura dos pisos é
  // assíncrona e arranca de um efeito; sem ele, o `setStoreysBusy(true)`
  // seria uma escrita de estado síncrona dentro do efeito (cascading
  // render, e o `react-hooks/set-state-in-effect` marca-o).
  const loadStoreys = useCallback(async () => {
    await Promise.resolve()
    const model = getModel()
    if (!model || !modelId) {
      setStoreys([])
      return
    }
    setStoreysBusy(true)
    try {
      // Cota declarada (Elevation do IFC levado ao mundo) sempre que o
      // leitor do IFC a dá; a geometria só entra como recurso.
      const declared = await declaredStoreyContext(modelId, model as never)
      const list = await collectStoreys(
        model as never,
        (localId) => viewerApi.getDescendants(modelId, localId),
        declared,
      )
      setStoreys(list)
      if (list.length > 0) {
        setFreeElevation(
          Number((list[0].elevation + PLAN_DEFAULT_CUT_OFFSET).toFixed(3)),
        )
      }
    } catch {
      setStoreys([])
    } finally {
      setStoreysBusy(false)
    }
  }, [getModel, modelId])

  // Mudar a georreferenciação ou a posição do modelo move-o no mundo: as
  // cotas dos pisos e a caixa do modelo têm de ser lidas outra vez.
  const placementKey = useAppStore((s) =>
    modelId
      ? JSON.stringify([
          s.geoOverrides[modelId] ?? null,
          s.geoBaselines[modelId] ?? null,
        ])
      : '',
  )

  useEffect(() => {
    if (!active || !modelId) return
    void (async () => {
      await Promise.resolve()
      await loadStoreys()
    })()
  }, [active, modelId, loadStoreys, placementKey])

  // Semente da origem do corte manual: centro geométrico do modelo.
  // Volta a semear quando o modelo muda de sítio (a origem antiga deixava
  // de estar dentro dele).
  const seedKey = modelId ? `${modelId}|${placementKey}` : null
  useEffect(() => {
    if (!active || !modelId || !seedKey || originSeeded === seedKey) return
    const model = getModel()
    if (!model) return
    void (async () => {
      await Promise.resolve()
      setOriginSeeded(seedKey)
      const box = await modelWorldBox(model as never)
      if (!box) return
      setWorldBox(box)
      setManualOrigin(boxCentre(box))
      setManualDepth(
        Math.max(
          5,
          Math.round(
            Math.max(box.max[0] - box.min[0], box.max[2] - box.min[2]) * 1.1,
          ),
        ),
      )
    })()
  }, [active, modelId, seedKey, originSeeded, getModel])

  // --- geração -------------------------------------------------------------
  /** Id da vista que está no ecrã. Numa ref porque o `runGeneration`
   *  precisa dele para decidir se as anotações se mantêm, e não pode
   *  depender do `current` sem se re-criar a cada geração. */
  const viewIdRef = useRef<string | null>(null)

  /** `notes` e `dims` explícitos mandam (abrir uma vista gravada); sem
   *  eles, as anotações e as cotas mantêm-se se for a MESMA vista
   *  (regenerar com outra cota de corte não deve deitar fora o que já se
   *  escreveu) e limpam-se se for outra. */
  const runGeneration = useCallback(
    async (
      view: View2DDef,
      notes?: Annotation2D[],
      dims?: Dimension2D[],
      /** Opções de GERAÇÃO forçadas. Servem para quem acabou de mudar o
       *  estado e não pode esperar pelo render seguinte (abrir uma vista
       *  gravada, ligar o interruptor das linhas ocultas): o `useState`
       *  só se vê no render a seguir, e sem isto a vista saía gerada com
       *  o valor anterior. */
      force?: { hiddenLines?: boolean },
    ) => {
      const model = getModel()
      if (!model || !modelId) {
        setError('Carrega um IFC primeiro.')
        return
      }
      const sameView = viewIdRef.current === view.id
      if (notes) setAnnotations(notes)
      else if (!sameView) setAnnotations([])
      if (dims) setDimensions(dims)
      else if (!sameView) setDimensions([])
      viewIdRef.current = view.id
      setEditing(null)
      setSelectedAnnot(null)
      setSelectedDim(null)
      setMeasurePts([])
      setMeasureCursor(null)
      setSnapHit(null)
      // O desenho vai ser outro: as chaves das polilinhas escolhidas
      // deixam de querer dizer o que queriam.
      setPickedKeys([])
      setHoverKey(null)
      setElemPick(null)
      setBusy(true)
      setError(null)
      // Deixar a UI pintar o estado ocupado antes do trabalho pesado.
      await new Promise((r) => setTimeout(r, 30))
      try {
        const drawing = await generateViewForModel(model as never, view, {
          bgMode: 'light',
          hiddenLines: force?.hiddenLines ?? hideHidden,
        })
        setCurrent({ view, drawing, modelId })
        setHiddenLayers(new Set())
        setDrawP1(null)
        if (view.kind !== 'plan') setTool((t) => (t === 'draw' ? 'none' : t))
        if (drawingPolylineCount(drawing).total === 0) {
          setError(
            'A vista saiu vazia: não há geometria nenhuma dentro da ' +
              'profundidade. Verifica a cota do plano e a profundidade.',
          )
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Falha a gerar a vista 2D.')
      } finally {
        setBusy(false)
      }
    },
    [getModel, modelId, hideHidden],
  )

  const createPlan = useCallback(
    (storey: Storey2D) => {
      const off = storeyCutOffset
      const d = planDepthFor(
        storey.elevation,
        off,
        storeys.map((s) => s.elevation),
      )
      setCutOffset(off)
      setDepth(d)
      const id = newViewId()
      planElevations.current.set(id, storey.elevation)
      void runGeneration(
        planViewDef({
          id,
          name: `Planta ${storey.name}`,
          elevation: storey.elevation,
          cutOffset: off,
          depth: d,
          scaleDenom: typeof scale === 'number' ? scale : undefined,
        }),
      )
    },
    [storeyCutOffset, storeys, scale, runGeneration],
  )

  /** Planta livre: a cota escrita é já a COTA DE CORTE absoluta, por
   *  isso o offset é zero. O campo "Corte (m)" da barra passa a 0 para
   *  o "Regenerar" não voltar a somar 1,20 m por baixo. */
  const createFreePlan = useCallback(() => {
    const id = newViewId()
    planElevations.current.set(id, freeElevation)
    freePlanIds.current.add(id)
    setCutOffset(0)
    setDepth(Math.max(0.01, freeDepth))
    void runGeneration(
      planViewDef({
        id,
        name: `Planta cota ${fmtNum(freeElevation)} m`,
        elevation: freeElevation,
        cutOffset: 0,
        depth: Math.max(0.01, freeDepth),
        scaleDenom: typeof scale === 'number' ? scale : undefined,
      }),
    )
  }, [freeElevation, freeDepth, scale, runGeneration])

  const createFromSectionBox = useCallback(() => {
    if (!sectionOrientation || !sectionExtents) {
      setError(
        'Liga a caixa de corte (modo 3D, separador Vista → Section box) e ' +
          'ajusta-a antes de criar o corte.',
      )
      return
    }
    const cam = viewerApi.getWorld()?.camera?.three
    const cameraPos: Vec3 | null = cam
      ? [cam.position.x, cam.position.y, cam.position.z]
      : null
    const view = sectionViewFromBox({
      orientation: sectionOrientation,
      extents: sectionExtents,
      name: uniqueViewName(saved, 'Corte da caixa'),
      id: newViewId(),
      cameraPos,
      scaleDenom: typeof scale === 'number' ? scale : undefined,
    })
    if (!view) {
      setError(
        'A caixa de corte não tem nenhuma face vertical utilizável. Roda ' +
          'ou redimensiona a caixa e tenta outra vez.',
      )
      return
    }
    setDepth(view.depth)
    void runGeneration(view)
  }, [sectionOrientation, sectionExtents, saved, scale, runGeneration])

  const createManualSection = useCallback(() => {
    let view: View2DDef
    try {
      view = sectionViewDef({
        id: newViewId(),
        name: uniqueViewName(saved, manualName.trim() || 'Corte'),
        origin: manualOrigin,
        azimuthDeg: manualAzimuth,
        depth: Math.max(0.1, manualDepth),
        scaleDenom: typeof scale === 'number' ? scale : undefined,
      })
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Corte inválido.')
      return
    }
    setDepth(view.depth)
    void runGeneration(view)
  }, [
    manualName,
    manualOrigin,
    manualAzimuth,
    manualDepth,
    saved,
    scale,
    runGeneration,
  ])

  const regenerate = useCallback(() => {
    if (!current) return
    const v = current.view
    let next: View2DDef
    if (v.kind === 'plan') {
      const elevation =
        planElevations.current.get(v.id) ?? v.origin[1] - cutOffset
      // Regenerar uma planta por piso com outra altura de corte passa a
      // ser a altura das plantas por piso seguintes.
      if (!freePlanIds.current.has(v.id)) setStoreyCutOffset(cutOffset)
      // `replanViewDef` e não `planViewDef`: este reconstruía os eixos e
      // a rotação da vista perdia-se a cada Regenerar.
      next = replanViewDef(v, {
        elevation,
        cutOffset,
        depth: Math.max(0.01, depth),
        scaleDenom: typeof scale === 'number' ? scale : undefined,
      })
    } else {
      next = {
        ...v,
        depth: Math.max(0.01, depth),
        scaleDenom: typeof scale === 'number' ? scale : v.scaleDenom,
      }
    }
    void runGeneration(next)
  }, [current, cutOffset, depth, scale, runGeneration])

  // --- vistas gravadas -----------------------------------------------------
  const commitSaved = useCallback(
    (next: SavedView2D[]) => {
      setSaved(next)
      if (sha256 && !persistSavedViews(sha256, next)) {
        pushToast(
          'Não foi possível gravar as vistas (armazenamento do browser cheio ou bloqueado).',
          { type: 'error' },
        )
      }
    },
    [sha256, pushToast],
  )

  // --- desenhar cortes sobre a planta --------------------------------------
  const isPlan = current?.view.kind === 'plan'

  /** Guarda a definição alterada da vista actual e regera o desenho.
   *
   *  Mesmo caminho do `flipCurrent`: a vista gravada, se existir, é
   *  actualizada, e o desenho é refeito a partir da definição nova. */
  const applyToCurrent = useCallback(
    (def: View2DDef) => {
      const entry = saved.find((v) => v.id === def.id)
      if (entry) commitSaved(upsertView(saved, { ...entry, def }))
      void runGeneration(def)
    },
    [saved, commitSaved, runGeneration],
  )

  /** Janela de recorte, a partir de dois cantos picados no desenho.
   *
   *  O motor já sabia recortar (descarta o que fica fora antes de
   *  calcular, corta as polilinhas na fronteira e usa a janela como
   *  bbox do desenho): só faltava por onde a definir. Afecta também o
   *  que sai no PDF e no DXF, como uma crop region de qualquer CAD. */
  const commitCrop = useCallback(
    (p1: [number, number], p2: [number, number]) => {
      if (!current) return
      const minX = Math.min(p1[0], p2[0])
      const maxX = Math.max(p1[0], p2[0])
      const minY = Math.min(p1[1], p2[1])
      const maxY = Math.max(p1[1], p2[1])
      if (maxX - minX < 1e-6 || maxY - minY < 1e-6) {
        setError('Janela demasiado pequena: os dois cantos têm de ser distintos.')
        return
      }
      applyToCurrent({ ...current.view, crop: { minX, minY, maxX, maxY } })
      // O comando termina aqui. Ficar ligado tinha duas consequências
      // más: as pegas de ajuste só aparecem com a ferramenta desligada,
      // por isso a janela ficava impossível de redimensionar, e o
      // arrasto seguinte começava uma janela NOVA em vez de ajustar a
      // que se acabou de traçar. Só há uma janela por vista: depois de
      // a definir não há nada que justifique continuar no comando.
      setTool('none')
      setDrawP1(null)
      setDrawCursor(null)
      pushToast('Janela de recorte definida. Arrasta as pegas para ajustar.', {
        type: 'success',
      })
    },
    [current, applyToCurrent, pushToast],
  )

  const clearCrop = useCallback(() => {
    if (!current?.view.crop) return
    const { crop: _crop, ...semCrop } = current.view
    applyToCurrent(semCrop)
    pushToast('Recorte removido', { type: 'info' })
  }, [current, applyToCurrent, pushToast])

  /** Roda a vista para o segmento picado ficar horizontal.
   *
   *  Roda os eixos do desenho, nunca o modelo: a geometria, a
   *  georreferenciação e as posições relativas entre modelos federados
   *  ficam intactas. Ver rotateViewDef em viewTypes.ts. */
  const commitAlign = useCallback(
    (p1: [number, number], p2: [number, number]) => {
      if (!current) return
      const ang = alignAngleDeg(p1, p2)
      if (ang === 0) {
        pushToast('O traço já está horizontal', { type: 'info' })
        return
      }
      const tinhaCrop = Boolean(current.view.crop)
      // SINAL: +ang, não -ang. Rodar a base por +deg roda as
      // coordenadas do desenho por -deg, que é o que leva o segmento de
      // `ang` a zero. Com o sinal trocado a vista ia para o DOBRO do
      // ângulo e o desenho ficava mais torto, com uma mensagem a dizer
      // que tinha alinhado. Ver o bloco "alinhar de verdade" nos testes.
      applyToCurrent(rotateViewDef(current.view, ang))
      // Também termina: alinhar é um gesto único, e ficar no comando
      // fazia o clique seguinte alinhar outra vez por cima.
      setTool('none')
      setDrawP1(null)
      setDrawCursor(null)
      pushToast(
        `Vista alinhada (${ang.toFixed(1)}°)` +
          (tinhaCrop ? '. A janela de recorte foi removida: estava desenhada nos eixos antigos.' : ''),
        { type: 'success' },
      )
    },
    [current, applyToCurrent, pushToast],
  )

  /** Roda a vista por um ângulo escrito. */
  const rotateCurrent = useCallback(
    (deg: number) => {
      if (!current || !Number.isFinite(deg) || deg === 0) return
      const tinhaCrop = Boolean(current.view.crop)
      applyToCurrent(rotateViewDef(current.view, deg))
      if (tinhaCrop) {
        pushToast(
          'A janela de recorte foi removida: estava desenhada nos eixos ' +
            'antigos.',
          { type: 'info' },
        )
      }
    },
    [current, applyToCurrent, pushToast],
  )

  const commitDrawnSection = useCallback(
    (p1: [number, number], p2: [number, number]) => {
      if (!current || current.view.kind !== 'plan') return
      // Sem caixa fiável, o corte fica com 3 m acima e abaixo do plano
      // de corte da planta: pouco, mas nunca absurdo.
      const minY = worldBox ? worldBox.min[1] : current.view.origin[1] - 3
      const maxY = worldBox ? worldBox.max[1] : current.view.origin[1] + 3
      const w1 = drawingPointToWorld(current.view, p1[0], p1[1])
      const w2 = drawingPointToWorld(current.view, p2[0], p2[1])
      const name = nextSectionMarkName(saved.map((v) => v.name))
      const view = sectionViewFromLine({
        p1: w1,
        p2: w2,
        minY,
        maxY,
        name,
        id: newViewId(),
        depth: Math.max(0.01, drawDepth),
        scaleDenom: typeof scale === 'number' ? scale : undefined,
      })
      if (!view) {
        setError('Traço demasiado curto: os dois cliques têm de ser distintos.')
        return
      }
      const entry: SavedView2D = {
        id: view.id,
        name,
        def: view,
        scale,
        paper,
        landscape,
        savedAt: Date.now(),
      }
      commitSaved(upsertView(saved, entry))
      pushToast(`${name} gravado`, { type: 'success' })
    },
    [
      current,
      worldBox,
      saved,
      drawDepth,
      scale,
      paper,
      landscape,
      commitSaved,
      pushToast,
    ],
  )

  // --- desenho visível (camadas filtradas) ---------------------------------
  const visibleDrawing = useMemo(() => {
    if (!current) return null
    const filtered = filterDrawingLayers(
      current.drawing,
      (n) => !hiddenLayers.has(n),
    )
    return applyLayerStyles(
      smoothArcs ? filtered : stripArcs(filtered),
      layerStyles,
    )
  }, [current, hiddenLayers, layerStyles, smoothArcs])

  useEffect(() => {
    if (!visibleDrawing || !precisaDeNomes(lentesDoCorte)) return
    const modelo = getModel()
    if (!modelo) return
    const ids = visibleDrawing.layers.flatMap((l) =>
      l.cut.map((p) => p.localId).filter((n): n is number => typeof n === 'number'),
    )
    let vivo = true
    void nomesDosElementos(modelo as unknown as Parameters<typeof nomesDosElementos>[0], ids).then((m) => {
      if (vivo) setNomesDoCorte(new Map(m))
    })
    return () => {
      vivo = false
    }
  }, [visibleDrawing, lentesDoCorte, getModel])

  const estiloDoCorte = useMemo<EstiloDoCorteNoPapel>(() => {
    const regras = regrasDeCorte.filter((r) => r.activa && r.valor.trim())
    if (regras.length === 0) return () => estiloBaseDeCorte
    const materiaisDe =
      modelId && regras.some((r) => r.tipo === 'material') ? leitorDeMateriais(modelId) : () => []
    return (localId, camada) =>
      estiloDoElemento(
        camada,
        () => (localId === undefined ? [] : materiaisDe(localId)),
        regras,
        estiloBaseDeCorte,
        modelId && localId !== undefined
          ? apanhaLentePara(lentesDoCorte, { modelId, localId, ifcClass: camada, nome: nomesDoCorte.get(localId) ?? null })
          : undefined,
      )
  }, [estiloBaseDeCorte, regrasDeCorte, modelId, lentesDoCorte, nomesDoCorte])

  /** O que vai para os ficheiros: o desenho visível MAIS as anotações e
   *  as cotas. A pré-visualização não as leva aqui de propósito, porque
   *  no ecrã são desenhadas pelo overlay (é lá que se arrastam, editam e
   *  seleccionam); ter as duas versões dava texto a dobrar. */
  const exportDrawing = useMemo(() => {
    if (!visibleDrawing) return null
    // A escala do papel é a da barra (ou a resolvida pelo "Ajustar à
    // folha"), não a que a vista tinha quando foi gerada: assim o SVG,
    // o PDF e o DXF saem os três à mesma escala.
    const atScale = drawingAtPaperScale(visibleDrawing, {
      paper,
      landscape,
      scaleDenom: scale,
    })
    if (annotations.length === 0 && dimensions.length === 0) {
      return atScale
    }
    const out: Drawing2D = { ...atScale }
    if (annotations.length > 0) out.annotations = annotations
    if (dimensions.length > 0) out.dimensions = dimensions
    return out
  }, [visibleDrawing, annotations, dimensions, paper, landscape, scale])

  /** Espessura e contraste do traço, já com a escolha do utilizador (ou
   *  o automático) resolvida. UMA fonte para o ecrã, o SVG, o PDF e o
   *  DXF: se cada um resolvesse por si, o papel deixava um dia de ser
   *  igual ao que se viu. */
  const weights = useMemo(
    () => resolveLineWeights(visibleDrawing, projWeight),
    [visibleDrawing, projWeight],
  )

  const svg = useMemo(() => {
    if (!visibleDrawing) return null
    try {
      // `localIds` só na pré-visualização: é o que liga o clique no
      // desenho ao elemento do modelo. O SVG exportado sai limpo.
      return drawingToSvg(visibleDrawing, {
        background: '#ffffff',
        fillCuts,
        estiloDoCorte,
        localIds: true,
        projWidthMm: weights.projWidthMm,
        projFade: weights.projFade,
      })
    } catch {
      return null
    }
  }, [visibleDrawing, fillCuts, weights, estiloDoCorte])

  const metrics = useMemo(
    () => (visibleDrawing ? svgMetrics(visibleDrawing) : null),
    [visibleDrawing],
  )

  const counts = useMemo(
    () => (visibleDrawing ? drawingPolylineCount(visibleDrawing) : null),
    [visibleDrawing],
  )

  const layout = useMemo(() => {
    if (!visibleDrawing) return null
    try {
      return pdfLayout(visibleDrawing, { paper, landscape, scaleDenom: scale })
    } catch {
      return null
    }
  }, [visibleDrawing, paper, landscape, scale])

  /** Marcas dos cortes gravados, em coordenadas do desenho da planta. */
  const marks = useMemo(() => {
    if (!current || current.view.kind !== 'plan' || !showMarks) return []
    const plan = current.view
    const out: {
      id: string
      label: string
      a: [number, number]
      b: [number, number]
      dir: [number, number]
    }[] = []
    for (const v of saved) {
      if (v.def.kind !== 'section' || !v.def.crop) continue
      try {
        const b = viewBasis(v.def)
        const endpoint = (x: number): Vec3 => [
          b.origin[0] + b.right[0] * x,
          b.origin[1] + b.right[1] * x,
          b.origin[2] + b.right[2] * x,
        ]
        const a2 = worldPointToDrawing(plan, endpoint(v.def.crop.minX))
        const b2 = worldPointToDrawing(plan, endpoint(v.def.crop.maxX))
        const planBasis = viewBasis(plan)
        const dir: [number, number] = [
          b.viewDir[0] * planBasis.right[0] +
            b.viewDir[1] * planBasis.right[1] +
            b.viewDir[2] * planBasis.right[2],
          b.viewDir[0] * planBasis.up[0] +
            b.viewDir[1] * planBasis.up[1] +
            b.viewDir[2] * planBasis.up[2],
        ]
        out.push({ id: v.id, label: markLabel(v.name), a: a2, b: b2, dir })
      } catch {
        /* vista gravada estragada: ignora-se a marca */
      }
    }
    return out
  }, [current, saved, showMarks])

  const previewRef = useRef<HTMLDivElement | null>(null)
  const naturalMm = useMemo(
    () => (metrics ? { w: metrics.width, h: metrics.height } : null),
    [metrics],
  )
  // A pré-visualização só existe no DOM em modo 2D e com um modelo
  // carregado; o hook precisa de saber para se ligar ao contentor certo.
  // O último argumento é o documento onde a pré-visualização vive:
  // destacar remonta o nó noutro documento, e sem isto a roda do rato e o
  // reenquadramento ficavam presos ao nó velho.
  const panzoom = useSvgPanZoom(
    previewRef,
    active && !!modelId,
    svg,
    naturalMm,
    detachedHost,
  )
  const overlayRef = useRef<SVGSVGElement | null>(null)

  /** Espessuras dos traços em unidades do SVG (mm de papel) que dão a
   *  espessura alvo em pixéis de ecrã com o zoom actual. Sem isto, um
   *  desenho enquadrado (zoom << 1) desenhava-se com traços de
   *  centésimas de pixel: aparecia uma folha em branco. */
  const strokeCss = useMemo(() => {
    const cut = strokeUnitsForPx(PREVIEW_CUT_PX, panzoom.state.scale)
    const proj = strokeUnitsForPx(PREVIEW_PROJ_PX, panzoom.state.scale)
    const sel = strokeUnitsForPx(PREVIEW_SELECT_PX, panzoom.state.scale)
    let css =
      `.v2d-paper path.cut{stroke-width:${cut.toFixed(6)}}` +
      `.v2d-paper path.proj{stroke-width:${proj.toFixed(6)}}`
    // Realce do elemento seleccionado. Estas regras vêm DEPOIS das de
    // cima de propósito: têm a mesma especificidade, e em caso de empate
    // ganha a última. As cores vencem os atributos `stroke=`/`fill=` do
    // SVG (atributos de apresentação perdem para qualquer regra CSS).
    if (selectedLocalId !== null) {
      css +=
        `.v2d-paper path[data-local-id="${selectedLocalId}"]` +
        `{stroke:${SELECT_STROKE};stroke-width:${sel.toFixed(6)}}` +
        `.v2d-paper path.poche[data-local-id="${selectedLocalId}"]` +
        `{fill:${SELECT_FILL}}`
    }
    // "Área do elemento": realce do que está debaixo do rato. É CSS puro
    // (o overlay não recebe os cliques neste modo, e sondar o DOM a cada
    // movimento do rato para pintar um traço era caro para o que vale).
    if (elemMode) {
      css +=
        `.v2d-paper path[data-local-id]{cursor:pointer}` +
        `.v2d-paper path[data-local-id]:hover` +
        `{stroke:${PICK_HOVER_STROKE};stroke-width:${sel.toFixed(6)}}` +
        `.v2d-paper path.poche[data-local-id]:hover{fill:${PICK_HOVER_FILL}}`
    }
    return css
  }, [panzoom.state.scale, selectedLocalId, elemMode])

  /** Unidades do SVG por pixel de ecrã: o overlay (marcas de corte,
   *  linha elástica) tem de crescer da mesma maneira. */
  const overlayUnit = strokeUnitsForPx(1, panzoom.state.scale)

  // --- seleccionar elementos pelo desenho ----------------------------------
  /** Onde o rato carregou, para distinguir um clique de um arrasto (o
   *  mesmo botão faz as duas coisas). */
  const clickStart = useRef<{ x: number; y: number } | null>(null)

  const onPreviewClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      // Com uma ferramenta activa o clique é dela (traço de corte,
      // etiqueta, vértice de medição), não da selecção.
      if (toolActive || !modelId || !current) return
      const s = clickStart.current
      if (s && Math.hypot(e.clientX - s.x, e.clientY - s.y) > CLICK_SLOP_PX) {
        return
      }
      // `elementFromPoint` é POR DOCUMENTO: com o 2D destacado, sondar o
      // `document` da janela principal devolvia o que estivesse debaixo
      // daquelas coordenadas no 3D. O documento certo vem do próprio nó
      // que recebeu o clique, e as coordenadas do evento já são relativas
      // à janela dele.
      const doc = e.currentTarget.ownerDocument
      const localId = pickLocalIdAt(
        (x, y) =>
          (doc ? doc.elementFromPoint(x, y) : null) as unknown as PickNode | null,
        e.clientX,
        e.clientY,
      )
      if (localId === null) {
        setElemPick(null)
        clearSelection()
        return
      }
      // Em "Área do elemento" o clique faz as duas coisas: selecciona
      // (que é o que já fazia, e traz o realce e o painel de
      // propriedades de graça) e mede a secção cortada.
      if (elemMode) setElemPick(localId)
      void viewerApi.selectByLocalId(modelId, localId)
    },
    [toolActive, modelId, current, elemMode, clearSelection],
  )

  const overlayPoint = useCallback(
    (e: React.PointerEvent): [number, number] | null => {
      const node = overlayRef.current
      if (!node || !metrics) return null
      const rect = node.getBoundingClientRect()
      const [sx, sy] = clientToSvg(rect, metrics, e.clientX, e.clientY)
      return metrics.fromSvg(sx, sy)
    },
    [metrics],
  )

  // --- ajuste da janela de recorte ------------------------------------------
  /** Começa o arrasto de uma pega. Fixa a caixa de partida e captura o
   *  ponteiro, para o arrasto continuar mesmo que o rato saia da pega. */
  const beginCropDrag = useCallback(
    (ev: React.PointerEvent, handle: CropHandle) => {
      const crop = current?.view.crop
      if (!crop) return
      const pt = overlayPoint(ev)
      if (!pt) return
      ev.stopPropagation()
      ev.preventDefault()
      // A captura é uma comodidade (deixa o arrasto continuar com o
      // rato fora da pega), não uma condição. Se falhar, o arrasto tem
      // de começar na mesma: sem este try a excepção abortava aqui e a
      // pega ficava a não fazer nada, sem nada a explicar porquê.
      try {
        ev.currentTarget.setPointerCapture(ev.pointerId)
      } catch {
        /* segue sem captura */
      }
      cropDragRef.current = { handle, from: pt, inicial: { ...crop } }
      setCropPreview({ ...crop })
    },
    [current, overlayPoint],
  )

  const moveCropDrag = useCallback(
    (ev: React.PointerEvent) => {
      const drag = cropDragRef.current
      if (!drag) return
      const pt = overlayPoint(ev)
      if (!pt) return
      ev.stopPropagation()
      setCropPreview(applyCropDrag(drag.inicial, drag.handle, drag.from, pt))
    },
    [overlayPoint],
  )

  /** Larga a pega e grava. Uma janela sem área é descartada e a anterior
   *  fica: gravar um recorte de zero por zero deixava a vista vazia sem
   *  o utilizador perceber porquê. */
  const endCropDrag = useCallback(
    (ev: React.PointerEvent) => {
      const drag = cropDragRef.current
      if (!drag) return
      ev.stopPropagation()
      cropDragRef.current = null
      const preview = cropPreview
      setCropPreview(null)
      if (!preview || !current) return
      if (!cropIsUsable(preview)) {
        pushToast('Janela demasiado pequena: fica como estava.', { type: 'info' })
        return
      }
      applyToCurrent({ ...current.view, crop: normalizeCrop(preview) })
    },
    [cropPreview, current, applyToCurrent, pushToast],
  )

  // --- medição -------------------------------------------------------------
  /** Resultado em vivo: enquanto se clica, entra também o ponto sob o
   *  cursor (a linha elástica conta para o comprimento acumulado). */
  const measureLive: MeasureResult | null = useMemo(() => {
    const pts = measureCursor ? [...measurePts, measureCursor] : measurePts
    return measure(pts, false)
  }, [measurePts, measureCursor])

  /** Quanto daria a área se se fechasse aqui. Só serve para a leitura da
   *  barra: fechar é uma decisão do utilizador (clicar no 1.º ponto ou
   *  carregar em "Fechar área"), não um palpite nosso. */
  const measureAreaHint: MeasureResult | null = useMemo(
    () => (measurePts.length >= 3 ? measure(measurePts, true) : null),
    [measurePts],
  )

  const clearMeasure = useCallback(() => {
    setMeasurePts([])
    setMeasureCursor(null)
    setSnapHit(null)
  }, [])

  /** Termina a medição: o resultado fica DESENHADO como cota (é a
   *  diferença toda para o que havia antes, em que o número desaparecia
   *  ao clicar noutro sítio). `asArea` fecha o polígono; com dois pontos
   *  é sempre uma distância. */
  const finishMeasure = useCallback(
    (asArea: boolean) => {
      const pts = measurePts
      if (pts.length < 2) return
      const kind = asArea && pts.length >= 3 ? 'area' : 'chain'
      const dim = makeDimension(pts, kind, newDimensionId())
      if (!dim) return
      setDimensions((list) => addDimension(list, dim))
      setSelectedDim(dim.id)
      clearMeasure()
      pushToast(`Cota ${dimensionLabel(dim)} criada`, { type: 'success' })
    },
    [measurePts, clearMeasure, pushToast],
  )

  const deleteDimension = useCallback((id: string) => {
    setDimensions((list) => removeDimension(list, id))
    setSelectedDim((cur) => (cur === id ? null : cur))
  }, [])

  // --- escolher geometria já desenhada -------------------------------------
  /** Índice das polilinhas do desenho VISÍVEL, com a caixa envolvente de
   *  cada uma. Reconstrói-se com o desenho, não com o zoom. */
  const pickIndex = useMemo(() => buildPickIndex(visibleDrawing), [visibleDrawing])

  const pickByKey = useMemo(() => {
    const m = new Map<string, PickTarget>()
    for (const t of pickIndex) m.set(t.key, t)
    return m
  }, [pickIndex])

  /** Raio da pontaria em unidades do desenho: `PICK_PX` de ecrã, seja
   *  qual for o zoom ou a escala de papel. */
  const pickRadius = useMemo(
    () => (metrics ? snapRadiusDrawing(PICK_PX, overlayUnit, metrics.k) : 0),
    [metrics, overlayUnit],
  )

  const pickedTargets = useMemo(
    () => pickedKeys.map((k) => pickByKey.get(k)).filter((t): t is PickTarget => !!t),
    [pickedKeys, pickByKey],
  )

  /** Encadeamento do que já está escolhido. A tolerância de fecho é a da
   *  pontaria, com tecto: ver `PICK_JOIN_MAX_M`. */
  const chain = useMemo(
    () =>
      chainPolylines(
        pickedTargets.map((t) => t.pl),
        Math.min(Math.max(pickRadius, 1e-6), PICK_JOIN_MAX_M),
      ),
    [pickedTargets, pickRadius],
  )

  /** Secção cortada do elemento escolhido (com os furos subtraídos). */
  const elemMeasureResult = useMemo(
    () => (elemPick === null ? null : elementMeasure(visibleDrawing, elemPick)),
    [elemPick, visibleDrawing],
  )

  const clearPick = useCallback(() => {
    setPickedKeys([])
    setHoverKey(null)
  }, [])

  /** Escolher (ou largar) uma polilinha. Um contorno FECHADO é uma
   *  fronteira por si só: substitui o que estivesse escolhido, em vez de
   *  ir para uma cadeia onde nunca encaixaria. */
  const togglePick = useCallback(
    (t: PickTarget) => {
      setPickedKeys((keys) => {
        if (keys.includes(t.key)) return keys.filter((k) => k !== t.key)
        if (t.closed) return [t.key]
        const firstClosed = keys[0] ? pickByKey.get(keys[0])?.closed : false
        return firstClosed ? [t.key] : [...keys, t.key]
      })
    },
    [pickByKey],
  )

  /** Grava o que está escolhido como cota. Fechado dá área e perímetro
   *  EXACTOS (com arcos: π·r² num círculo, r·|Δ| no perímetro); aberto
   *  fica uma cota encadeada, que é o que um troço aberto vale. */
  const saveChainDimension = useCallback(() => {
    if (!chain.boundary || chain.points.length < 2) return
    const dim = chain.closed
      ? makeDimension(chain.points, 'area', newDimensionId(), {
          area: chain.area,
          perimeter: chain.length,
        })
      : makeDimension(chain.points, 'chain', newDimensionId())
    if (!dim) return
    setDimensions((list) => addDimension(list, dim))
    setSelectedDim(dim.id)
    clearPick()
    pushToast(`Cota ${dimensionLabel(dim)} criada`, { type: 'success' })
  }, [chain, clearPick, pushToast])

  /** Leitura dos modos de escolha, para a barra. */
  const pickReadout = useMemo(() => {
    if (elemMode) {
      if (!elemMeasureResult || elemMeasureResult.loops === 0) {
        return elemPick === null
          ? 'Clica num elemento do desenho'
          : 'Esse elemento não tem contorno FECHADO nesta vista'
      }
      const m = elemMeasureResult
      const furos = m.holes > 0 ? ` · ${m.holes} furo${m.holes === 1 ? '' : 's'}` : ''
      return `A = ${fmtPt(m.area)} m² · perímetro ${fmtPt(m.perimeter)} m${furos}`
    }
    if (pickedTargets.length === 0) return 'Clica num contorno ou num troço'
    const n = `${pickedTargets.length} troço${pickedTargets.length === 1 ? '' : 's'}`
    const solto = chain.loose > 0 ? ` · ${chain.loose} solto${chain.loose === 1 ? '' : 's'}` : ''
    if (chain.closed) {
      return `${n} · A = ${fmtPt(chain.area)} m² · perímetro ${fmtPt(chain.length)} m${solto}`
    }
    return `${n} · ${fmtPt(chain.length)} m (por fechar)${solto}`
  }, [elemMode, elemMeasureResult, elemPick, pickedTargets.length, chain])

  /** O que o overlay realça nos modos de escolha. */
  const pickHighlight = useMemo(() => {
    const hover =
      pickMode && hoverKey && !pickedKeys.includes(hoverKey)
        ? (pickByKey.get(hoverKey)?.pl ?? null)
        : null
    if (elemMode) {
      const m = elemMeasureResult
      const region =
        m && m.outline.length >= 3 ? { outline: m.outline, rings: m.rings } : null
      return {
        picked: [],
        hover: null,
        region,
        label: region ? `A = ${fmtPt(m!.area)} m²` : '',
        labelAt: region ? polygonCentroid(m!.outline) : null,
      }
    }
    const region =
      chain.closed && chain.points.length >= 3
        ? { outline: chain.points, rings: [] }
        : null
    return {
      picked: pickedTargets.map((t) => t.pl),
      hover,
      region,
      label: region ? `A = ${fmtPt(chain.area)} m²` : '',
      labelAt: region ? polygonCentroid(chain.points) : null,
    }
  }, [
    pickMode,
    elemMode,
    hoverKey,
    pickedKeys,
    pickByKey,
    pickedTargets,
    chain,
    elemMeasureResult,
  ])

  /** Grava a área do elemento como cota: contorno exterior mais os furos
   *  (que ficam tracejados no desenho e nos exports) e os valores
   *  exactos. */
  const saveElementDimension = useCallback(() => {
    const m = elemMeasureResult
    if (!m || m.outline.length < 3) return
    const dim = makeDimension(m.outline, 'area', newDimensionId(), {
      rings: m.rings,
      area: m.area,
      perimeter: m.perimeter,
    })
    if (!dim) return
    setDimensions((list) => addDimension(list, dim))
    setSelectedDim(dim.id)
    setElemPick(null)
    pushToast(`Cota ${dimensionLabel(dim)} criada`, { type: 'success' })
  }, [elemMeasureResult, pushToast])

  // --- object snap ---------------------------------------------------------
  /** Índice espacial do desenho VISÍVEL: o que está numa camada
   *  desligada não se vê, logo não agarra. Reconstrói-se com o desenho,
   *  não com o zoom (o zoom só muda o RAIO). */
  const snapIndex = useMemo(() => buildSnapIndex(visibleDrawing), [visibleDrawing])

  /** Raio em unidades do desenho que dá `SNAP_PX` no ecrã, seja qual for
   *  o zoom ou a escala de papel. */
  const snapRadius = useMemo(
    () => (metrics ? snapRadiusDrawing(SNAP_PX, overlayUnit, metrics.k) : 0),
    [metrics, overlayUnit],
  )

  /** Ponto anterior do gesto em curso: é a ele que o orto se agarra. */
  const gestureAnchor: Point2 | null = drawMode || cropMode || alignMode
    ? drawP1
    : measureMode && measurePts.length > 0
      ? measurePts[measurePts.length - 1]
      : null

  /** Ponto que o gesto deve usar. Por esta ordem:
   *  1. Shift (orto) manda: o ponto tem de ficar no eixo do anterior, e
   *     um ponto agarrado noutro sítio desfazia isso;
   *  2. Alt desliga o snap enquanto estiver carregado;
   *  3. senão, agarra-se à geometria e o clique usa o ponto EXACTO do
   *     desenho, não o do rato. */
  const pickPoint = useCallback(
    (
      e: React.PointerEvent,
      anchor: Point2 | null,
      /** Recorte: uma janela de vista não é geometria e não se agarra a
       *  pontos do modelo. Obrigá-la ao snap impedia sequer de a
       *  traçar onde se queria. */
      semSnap = false,
    ): { p: Point2; hit: SnapPoint | null } | null => {
      const raw = overlayPoint(e)
      if (!raw) return null
      if (e.shiftKey && anchor) return { p: orthoPoint(anchor, raw), hit: null }
      if (semSnap) return { p: raw, hit: null }
      if (!snapOn || e.altKey || snapRadius <= 0) return { p: raw, hit: null }
      const hit = resolveSnap(snapIndex, raw[0], raw[1], snapRadius)
      // Cópia: o ponto do índice é partilhado e não pode ir parar a uma
      // lista de medição que depois se altera.
      if (!hit) return { p: raw, hit: null }
      return { p: [hit.p[0], hit.p[1]], hit }
    },
    [overlayPoint, snapOn, snapRadius, snapIndex],
  )

  /** Só troca o estado quando a marca muda mesmo: o `pointermove` dispara
   *  dezenas de vezes por segundo e um `setState` por evento fazia o
   *  React re-renderizar o desenho todo à toa. */
  const showSnap = useCallback((hit: SnapPoint | null) => {
    setSnapHit((cur) => {
      if (cur === hit) return cur
      if (!cur || !hit) return hit
      if (cur.kind === hit.kind && cur.p[0] === hit.p[0] && cur.p[1] === hit.p[1]) {
        return cur
      }
      return hit
    })
  }, [])

  // --- gestos sobre o overlay ----------------------------------------------
  const onOverlayDown = useCallback(
    (e: React.PointerEvent<SVGSVGElement>) => {
      if (!toolActive) return
      e.stopPropagation()
      // Escolher linhas não precisa de snap: o alvo é a geometria
      // inteira, não um ponto dela.
      if (pickMode) {
        const raw = overlayPoint(e)
        if (!raw) return
        const t = nearestTarget(pickIndex, raw[0], raw[1], pickRadius)
        if (!t) return
        // Um duplo clique é dois `pointerdown`, e o segundo largava o
        // troço que o primeiro tinha escolhido: o gesto de terminar
        // desfazia-se a si próprio. Dentro da janela do duplo clique, o
        // segundo clique no MESMO troço não larga nada.
        const now = e.timeStamp || Date.now()
        const last = lastPickRef.current
        lastPickRef.current = { key: t.key, t: now }
        if (last && last.key === t.key && now - last.t < DOUBLE_CLICK_MS) return
        togglePick(t)
        return
      }
      const picked = pickPoint(e, gestureAnchor, cropMode)
      if (!picked) return
      const p = picked.p
      showSnap(picked.hit)
      if (drawMode || cropMode || alignMode) {
        if (!drawP1) {
          setDrawP1(p)
          setDrawCursor(p)
          return
        }
        if (drawMode) commitDrawnSection(drawP1, p)
        else if (cropMode) commitCrop(drawP1, p)
        else commitAlign(drawP1, p)
        setDrawP1(null)
        setDrawCursor(null)
        return
      }
      if (annotMode) {
        // Clique no vazio abre a caixa de texto de uma etiqueta NOVA.
        // Os cliques sobre etiquetas existentes não chegam aqui: o grupo
        // de cada uma pára a propagação.
        setSelectedAnnot(null)
        setEditing({ id: null, at: p, text: '' })
        return
      }
      if (measureMode) {
        // Clicar no vazio larga a cota seleccionada (clicar EM CIMA de
        // uma cota não chega aqui: o glifo pára a propagação).
        setSelectedDim(null)
        // Clicar em cima do primeiro ponto fecha o polígono e deixa a
        // cota de área desenhada.
        if (measurePts.length >= 3 && metrics) {
          const [fx, fy] = metrics.toSvg(measurePts[0][0], measurePts[0][1])
          const [cx, cy] = metrics.toSvg(p[0], p[1])
          if (Math.hypot(cx - fx, cy - fy) <= overlayUnit * MEASURE_SNAP_PX) {
            finishMeasure(true)
            return
          }
        }
        // O cursor NÃO se semeia com o ponto clicado: senão o último
        // vértice entrava duas vezes na leitura em vivo e dois cliques
        // apareciam como "Comprimento" em vez de "Distância". A linha
        // elástica nasce no primeiro `pointermove`, que é quando faz
        // sentido haver uma.
        setMeasurePts((pts) => [...pts, p])
      }
    },
    [
      toolActive,
      drawMode,
      annotMode,
      measureMode,
      drawP1,
      measurePts,
      metrics,
      overlayUnit,
      pickPoint,
      gestureAnchor,
      showSnap,
      commitDrawnSection,
      finishMeasure,
      pickMode,
      pickIndex,
      pickRadius,
      overlayPoint,
      togglePick,
    ],
  )

  const onOverlayMove = useCallback(
    (e: React.PointerEvent<SVGSVGElement>) => {
      // Arrastar uma etiqueta ganha a tudo o resto.
      const drag = annotDragRef.current
      if (drag) {
        if (!drag.captured) {
          // Ainda dentro da folga: é um clique, não um arrasto.
          const moved = Math.hypot(e.clientX - drag.x0, e.clientY - drag.y0)
          if (moved <= ANNOT_DRAG_SLOP_PX) return
          // A partir daqui é arrasto a sério: agarra-se o ponteiro para
          // a etiqueta não fugir se o rato sair do desenho. `try`
          // porque `setPointerCapture` atira se o ponteiro já não
          // estiver activo, e perder a captura não é razão para perder
          // o arrasto.
          try {
            overlayRef.current?.setPointerCapture?.(e.pointerId)
            drag.captured = true
          } catch {
            drag.captured = false
          }
        }
        const p = overlayPoint(e)
        if (p) {
          setAnnotations((list) =>
            moveAnnotation(list, drag.id, p[0] + drag.dx, p[1] + drag.dy),
          )
        }
        return
      }
      if (!toolActive) return
      // Realce do que é escolhível: só troca o estado quando muda mesmo
      // (o `pointermove` dispara dezenas de vezes por segundo).
      if (pickMode) {
        const raw = overlayPoint(e)
        const t = raw
          ? nearestTarget(pickIndex, raw[0], raw[1], pickRadius)
          : null
        setHoverKey((cur) => (cur === (t?.key ?? null) ? cur : t?.key ?? null))
        return
      }
      // Uma só resolução por movimento: a marca do snap e o ponto da
      // linha elástica são o MESMO ponto, senão a marca dizia uma coisa
      // e o clique fazia outra.
      const picked = pickPoint(e, gestureAnchor, cropMode)
      if (!picked) return
      showSnap(picked.hit)
      if (drawMode || cropMode || alignMode) {
        if (drawP1) setDrawCursor(picked.p)
        return
      }
      if (measureMode && measurePts.length > 0) setMeasureCursor(picked.p)
    },
    [
      toolActive,
      drawMode,
      cropMode,
      alignMode,
      drawP1,
      measureMode,
      measurePts.length,
      overlayPoint,
      pickPoint,
      gestureAnchor,
      showSnap,
      pickMode,
      pickIndex,
      pickRadius,
    ],
  )

  /** Sair do desenho apaga a marca: uma marca de snap parada num canto
   *  com o rato noutro sítio é uma mentira. O mesmo vale para o realce
   *  do que era escolhível. */
  const onOverlayLeave = useCallback(() => {
    showSnap(null)
    setHoverKey(null)
  }, [showSnap])

  const onOverlayUp = useCallback((e: React.PointerEvent<SVGSVGElement>) => {
    const drag = annotDragRef.current
    if (!drag) return
    annotDragRef.current = null
    if (drag.captured) e.currentTarget.releasePointerCapture?.(e.pointerId)
  }, [])

  const onOverlayDoubleClick = useCallback(() => {
    // Duplo clique termina a medição (convenção de CAD) e deixa a cota
    // no desenho. Nas etiquetas o duplo clique é tratado no próprio
    // grupo.
    if (measureMode && measurePts.length >= 2) finishMeasure(false)
    if (pickMode && pickedKeys.length > 0) saveChainDimension()
  }, [
    measureMode,
    measurePts.length,
    finishMeasure,
    pickMode,
    pickedKeys.length,
    saveChainDimension,
  ])

  /** Começa a arrastar uma etiqueta. Guarda o desvio entre a âncora e o
   *  ponto agarrado para a etiqueta não saltar para debaixo do cursor. */
  const onAnnotDown = useCallback(
    (e: React.PointerEvent<SVGGElement>, a: Annotation2D) => {
      e.stopPropagation()
      setSelectedAnnot(a.id)
      const p = overlayPoint(e)
      if (!p) return
      // Sem `setPointerCapture` aqui: ver `annotDragRef`. A captura só
      // entra quando o arrasto começa mesmo.
      annotDragRef.current = {
        id: a.id,
        dx: a.x - p[0],
        dy: a.y - p[1],
        x0: e.clientX,
        y0: e.clientY,
        captured: false,
      }
    },
    [overlayPoint],
  )

  // Teclado das ferramentas: Esc cancela por camadas (o que está em
  // curso primeiro, a ferramenta só depois), Enter fecha a medição,
  // Delete apaga a etiqueta seleccionada.
  useEffect(() => {
    if (!active || !hostWin) return
    if (tool === 'none' && !selectedAnnot && !selectedDim) return
    // (o teclado dos modos de escolha entra no mesmo tratador)
    const onKey = (e: KeyboardEvent) => {
      // A caixa de texto trata as suas próprias teclas.
      if (editing) return
      // Alt desliga o snap enquanto estiver carregado: apagar já a marca
      // evita que ela fique a apontar um canto onde o clique não vai
      // cair. Volta no primeiro movimento com o Alt largado.
      if (e.key === 'Alt') {
        setSnapHit(null)
        return
      }
      if (e.key === 'Escape') {
        // O Escape não pode ser roubado por coisas que não são a
        // ferramenta que está a correr. A ordem anterior era uma
        // cascata única, e por isso uma anotação seleccionada minutos
        // antes engolia o primeiro Escape: carregava-se, não se via
        // nada acontecer, e parecia que o comando não fechava.
        e.preventDefault()
        if (tool !== 'none') {
          const aMeio =
            ((drawMode || cropMode || alignMode) && drawP1 !== null) ||
            (measureMode && measurePts.length > 0) ||
            (pickMode && pickedKeys.length > 0) ||
            (elemMode && elemPick !== null)
          if (aMeio) {
            // Primeiro Escape: larga o que está a meio, fica no comando.
            setDrawP1(null)
            setDrawCursor(null)
            if (measureMode) clearMeasure()
            if (pickMode) clearPick()
            if (elemMode) setElemPick(null)
            return
          }
          // Segundo Escape (ou o primeiro, com nada a meio): sai do
          // comando e leva consigo tudo o que lhe pertencia.
          setTool('none')
          setDrawP1(null)
          setDrawCursor(null)
          clearMeasure()
          clearPick()
          setElemPick(null)
          return
        }
        // Sem ferramenta a correr, o Escape desfaz selecções.
        if (selectedAnnot) setSelectedAnnot(null)
        else if (selectedDim) setSelectedDim(null)
        return
      }
      if (e.key === 'Enter' && measureMode && measurePts.length >= 2) {
        e.preventDefault()
        finishMeasure(false)
        return
      }
      if (e.key === 'Enter' && pickMode && pickedKeys.length > 0) {
        e.preventDefault()
        saveChainDimension()
        return
      }
      if (e.key === 'Enter' && elemMode && elemMeasureResult) {
        e.preventDefault()
        saveElementDimension()
        return
      }
      if (e.key === 'Delete' || e.key === 'Backspace') {
        if (selectedAnnot) {
          e.preventDefault()
          setAnnotations((list) => removeAnnotation(list, selectedAnnot))
          setSelectedAnnot(null)
        } else if (selectedDim) {
          e.preventDefault()
          deleteDimension(selectedDim)
        }
      }
    }
    // Na janela onde o desenho está, não na principal.
    hostWin.addEventListener('keydown', onKey)
    return () => hostWin.removeEventListener('keydown', onKey)
  }, [
    active,
    hostWin,
    tool,
    // Derivados do `tool`, mas o Escape lê-os para saber a que
    // ferramenta pertence o que está a meio.
    drawMode,
    cropMode,
    alignMode,
    drawP1,
    editing,
    measureMode,
    measurePts.length,
    selectedAnnot,
    selectedDim,
    clearMeasure,
    finishMeasure,
    deleteDimension,
    pickMode,
    pickedKeys.length,
    clearPick,
    saveChainDimension,
    elemMode,
    elemPick,
    elemMeasureResult,
    saveElementDimension,
  ])

  /** Confirma a caixa de texto inline (etiqueta nova ou edição). */
  const commitEditing = useCallback(() => {
    const ed = editing
    if (!ed) return
    if (ed.id) {
      const id = ed.id
      setAnnotations((list) => setAnnotationText(list, id, ed.text))
    } else {
      const a = makeAnnotation(ed.at, ed.text, newAnnotationId())
      if (a) {
        setAnnotations((list) => addAnnotation(list, a))
        setSelectedAnnot(a.id)
      }
    }
    setEditing(null)
  }, [editing])

  // --- exportação ----------------------------------------------------------
  const baseFileName = current
    ? sanitizeFileName(current.view.name, 'vista_2d')
    : 'vista_2d'

  const exportSvg = () => {
    if (!exportDrawing) return
    downloadBlob(
      drawingToSvg(exportDrawing, {
        background: '#ffffff',
        fillCuts,
        estiloDoCorte,
        projWidthMm: weights.projWidthMm,
        projFade: weights.projFade,
      }),
      `${baseFileName}.svg`,
      'image/svg+xml',
    )
    pushToast(`SVG exportado (${baseFileName}.svg)`, { type: 'success' })
  }

  const exportPdf = () => {
    if (!exportDrawing) return
    // A escala fixa não encolhe o desenho: se não cabe, o PDF sai cortado
    // nas bordas. Pergunta-se antes, na janela onde se carregou.
    if (
      layout?.overflow &&
      !hostWin?.confirm(
        `${pdfOverflowMessage(layout, paper)} Exportar mesmo assim?`,
      )
    ) {
      return
    }
    const bytes = drawingToPdf(exportDrawing, {
      paper,
      landscape,
      scaleDenom: scale,
      fillCuts,
      estiloDoCorte,
      projWidthMm: weights.projWidthMm,
      projFade: weights.projFade,
    })
    downloadBlob(bytes.slice(), `${baseFileName}.pdf`, 'application/pdf')
    pushToast(`PDF exportado (${baseFileName}.pdf)`, { type: 'success' })
  }

  const exportDxf = () => {
    if (!exportDrawing) return
    // Gravado em Windows-1252, a code page que o cabeçalho declara: um
    // Blob da string sairia em UTF-8 e os acentos e o "²" chegavam
    // estragados ao CAD.
    downloadBlob(
      dxfToBytes(drawingToDxf(exportDrawing, { projFade: weights.projFade })),
      `${baseFileName}.dxf`,
      'image/vnd.dxf',
    )
    pushToast(
      fillCuts
        ? `DXF exportado (${baseFileName}.dxf), sem preenchimentos: o R12 não tem HATCH, aplica-o no CAD sobre os contornos fechados`
        : `DXF exportado (${baseFileName}.dxf)`,
      { type: 'success' },
    )
  }

  const saveCurrent = () => {
    if (!current) return
    if (!sha256) {
      pushToast('Modelo sem identificador: não dá para gravar vistas.', {
        type: 'error',
      })
      return
    }
    commitSaved(
      upsertView(saved, {
        id: current.view.id,
        name: current.view.name,
        def: current.view,
        scale,
        paper,
        landscape,
        cutOffset: current.view.kind === 'plan' ? cutOffset : undefined,
        layerStyles:
          Object.keys(layerStyles).length > 0 ? layerStyles : undefined,
        fillCuts,
        smoothArcs,
        projWeight,
        hiddenLines: hideHidden,
        annotations: annotations.length > 0 ? annotations : undefined,
        dimensions: dimensions.length > 0 ? dimensions : undefined,
        savedAt: Date.now(),
      }),
    )
    pushToast(`Vista "${current.view.name}" gravada`, { type: 'success' })
  }

  const openSaved = useCallback(
    (v: SavedView2D) => {
      setScale(v.scale)
      setPaper(v.paper)
      setLandscape(v.landscape)
      setDepth(v.def.depth)
      // Campos da terceira leva: uma vista gravada antes deles não os tem
      // e abre com os valores por omissão (paleta e poché ligado).
      setLayerStyles(v.layerStyles ?? {})
      setFillCuts(v.fillCuts ?? true)
      setSmoothArcs(v.smoothArcs ?? true)
      setProjWeight(sanitizeProjWeight(v.projWeight) ?? 'auto')
      setHideHidden(v.hiddenLines === true)
      if (v.def.kind === 'plan') {
        const off = v.cutOffset ?? PLAN_DEFAULT_CUT_OFFSET
        setCutOffset(off)
        planElevations.current.set(v.def.id, v.def.origin[1] - off)
        // Uma planta gravada com offset 0 é uma planta livre.
        if (off === 0) freePlanIds.current.add(v.def.id)
      }
      // Listas sempre explícitas (mesmo vazias): abrir uma vista sem
      // anotações nem cotas tem de LIMPAR as da vista anterior.
      void runGeneration(v.def, v.annotations ?? [], v.dimensions ?? [], {
        hiddenLines: v.hiddenLines === true,
      })
    },
    [runGeneration],
  )

  // `prompt`/`confirm` são POR JANELA: destacado, a caixa tem de nascer
  // na janela onde se carregou no botão, senão o utilizador ficava com a
  // aplicação bloqueada à espera de uma resposta que estava no outro ecrã.
  const renameSaved = (v: SavedView2D) => {
    const name = hostWin?.prompt('Novo nome da vista', v.name) ?? null
    if (name === null) return
    commitSaved(renameView(saved, v.id, name))
  }

  const deleteSaved = (v: SavedView2D) => {
    if (!hostWin?.confirm(`Apagar a vista "${v.name}"?`)) return
    commitSaved(removeView(saved, v.id))
  }

  const duplicateSaved = (v: SavedView2D) => {
    commitSaved(duplicateView(saved, v.id, newViewId()))
  }

  /** Inverte a direcção de observação do corte actual (e actualiza a
   *  vista gravada correspondente, se existir). */
  const flipCurrent = () => {
    if (!current || current.view.kind !== 'section') return
    const flipped = flipSectionView(current.view)
    const entry = saved.find((v) => v.id === flipped.id)
    if (entry) commitSaved(upsertView(saved, { ...entry, def: flipped }))
    void runGeneration(flipped)
  }

  /** Ponte 2D -> 3D: leva a câmara e a caixa de corte à fatia desenhada
   *  e SAI do modo 2D. A vista fica como está: voltar ao 2D reabre-a. */
  const view3d = async () => {
    if (!current) return
    // Sair do 2D PRIMEIRO: o `goToView3D` faz a transição da câmara
    // animada, e esperar por ela antes de trocar de modo dava um botão
    // que parecia não fazer nada durante meio segundo (e a animação
    // acontecia toda com o 3D escondido).
    //
    // Destacado NÃO se sai de lado nenhum: o 3D já está na principal e o
    // desenho fica onde está. Só se chama a principal para a frente (o
    // browser pode ignorar, e nesse caso não se perde nada).
    if (detached) {
      try {
        window.focus()
      } catch {
        /* o browser pode recusar dar foco: a câmara vai na mesma */
      }
    } else {
      setSurface('3d')
    }
    const r = await goToView3D(current.view, worldBox)
    if (!r.ok) {
      pushToast(r.reason ?? 'Não foi possível ir para a vista no 3D.', {
        type: 'error',
      })
    }
  }

  // --- escolha de vista no telemóvel ---------------------------------------
  const mobileOptions = useMemo(
    () => mobileViewOptions(saved, storeys),
    [saved, storeys],
  )
  const [mobileKey, setMobileKey] = useState('')
  const onMobilePick = useCallback(
    (key: string) => {
      setMobileKey(key)
      const opt = mobileOptions.find((o) => o.key === key)
      if (!opt) return
      if (opt.kind === 'saved') {
        const v = saved.find((s) => s.id === opt.savedId)
        if (v) openSaved(v)
        return
      }
      const s = storeys[opt.storeyIndex ?? -1]
      if (s) createPlan(s)
    },
    [mobileOptions, saved, storeys, openSaved, createPlan],
  )

  if (!active) return null

  /** Casca da superfície. Embutida ocupa a área central por cima do
   *  canvas (`absolute inset-0 z-30`); destacada é o corpo todo da
   *  janela e não tem nada por baixo de que se sobrepor. */
  const shellClass = detached
    ? 'flex h-full min-h-0 min-w-0 flex-col overflow-hidden bg-[var(--color-surface)]'
    : 'absolute inset-0 z-30 flex min-w-0 flex-col overflow-hidden bg-[var(--color-surface)]'

  /** Entrega a superfície: embutida tal e qual, destacada por portal
   *  para o documento da janela externa. Enquanto a janela não estiver
   *  pronta devolve-se null (é um frame). O `ToastHost` vai junto: é só
   *  store, e sem ele as confirmações de exportação apareciam no ecrã
   *  onde o utilizador não está a olhar. */
  const deliver = (shell: React.ReactNode) => {
    if (!detached) return shell
    if (!detachedHost) return null
    return createPortal(
      <>
        {shell}
        <ToastHost />
      </>,
      detachedHost,
    )
  }

  const viewBadge = current ? (
    <span
      data-testid="views2d-current-name"
      className="truncate rounded bg-[var(--color-accent-bg)] px-1.5 py-px text-[10px] font-semibold uppercase tracking-wider text-[var(--color-accent)]"
    >
      {current.view.kind === 'plan' ? 'Planta' : 'Corte'} · {current.view.name}
    </span>
  ) : null

  // --- o desenho (partilhado por desktop e telemóvel) ----------------------
  const drawingArea = (
    <div
      ref={previewRef}
      onPointerDown={
        toolActive
          ? undefined
          : (e) => {
              clickStart.current = { x: e.clientX, y: e.clientY }
              panzoom.handlers.onPointerDown(e)
            }
      }
      onClick={onPreviewClick}
      onPointerMove={toolActive ? undefined : panzoom.handlers.onPointerMove}
      onPointerUp={toolActive ? undefined : panzoom.handlers.onPointerUp}
      onPointerCancel={toolActive ? undefined : panzoom.handlers.onPointerUp}
      data-testid="views2d-preview"
      // `touch-none`: sem isto, num ecrã táctil (ou com caneta) o
      // arrasto era comido pelo scroll da página em vez de deslocar o
      // desenho, e o pinch dava zoom à página toda.
      className={`relative min-h-0 min-w-0 flex-1 touch-none overflow-hidden border-t border-[var(--color-border)] bg-white ${
        toolActive
          ? 'cursor-crosshair'
          : elemMode
            ? 'cursor-pointer'
            : 'cursor-grab active:cursor-grabbing'
      }`}
      title={
        drawMode
          ? 'Clica dois pontos para definir o traço de corte'
          : annotMode
            ? 'Clica onde queres a etiqueta'
            : measureMode
              ? 'Clica os vértices; Enter ou duplo clique terminam e deixam a cota'
              : pickMode
                ? 'Clica nas linhas do desenho; um contorno fechado dá logo a ' +
                  'área, os troços abertos encadeiam-se. Enter grava a cota.'
                : elemMode
                  ? 'Clica num elemento para medir a área da secção cortada'
                  : 'Arrastar para deslocar, roda do rato (ou dois dedos) para ' +
                    'dar zoom. Clicar numa linha ou numa zona cortada selecciona ' +
                    'esse elemento; clicar no vazio limpa a selecção.'
      }
    >
      {svg && metrics ? (
        <div
          className="absolute left-0 top-0 origin-top-left"
          style={{
            transform: `translate(${panzoom.state.x}px, ${panzoom.state.y}px) scale(${panzoom.state.scale})`,
          }}
        >
          {/* As espessuras vêm do writer em mm de papel (0,35 e 0,13).
              Enquadrado, o zoom é muito menor do que 1 e isso dá traços
              de centésimas de pixel: reescrevem-se aqui, em pixéis de
              ecrã. O SVG/PDF exportado não é afectado. */}
          <style>{strokeCss}</style>
          <div
            className="v2d-paper relative"
            style={{
              width: `${metrics.width}mm`,
              height: `${metrics.height}mm`,
            }}
          >
            {/* O SVG é gerado pelo nosso próprio writer a partir de
                números; não passa por aqui nada vindo do utilizador. */}
            <div dangerouslySetInnerHTML={{ __html: svg }} />
            <svg
              ref={overlayRef}
              xmlns="http://www.w3.org/2000/svg"
              className="absolute left-0 top-0"
              width={`${metrics.width}mm`}
              height={`${metrics.height}mm`}
              viewBox={`0 0 ${metrics.width} ${metrics.height}`}
              style={{ pointerEvents: toolActive ? 'auto' : 'none' }}
              onPointerDown={onOverlayDown}
              onPointerMove={onOverlayMove}
              onPointerUp={onOverlayUp}
              onPointerLeave={onOverlayLeave}
              // Só no modo Anotar, e é o que faz a ferramenta funcionar.
              // A caixa de texto nasce no `pointerdown` e o React
              // monta-a (e dá-lhe o foco) ainda dentro desse evento. A
              // seguir vem o `mousedown`, cuja acção por omissão é
              // mudar o foco para o ascendente focável do alvo: o
              // overlay não é focável, portanto o foco ia parar ao
              // <body> e a caixa recém-nascida levava um `blur`. O
              // `onBlur` confirma, o texto está vazio, a etiqueta é
              // descartada e a caixa desaparece no mesmo clique: dava
              // a impressão de que Anotar não fazia nada. Travar a
              // acção por omissão do `mousedown` é a única maneira de
              // o foco lá ficar. Nos outros modos não se mexe: aí o
              // clique não abre caixa nenhuma e não há foco a
              // proteger.
              onMouseDown={annotMode ? (e) => e.preventDefault() : undefined}
              onDoubleClick={onOverlayDoubleClick}
              data-testid="views2d-overlay"
            >
              {marks.map((m) => (
                <MarkGlyph
                  key={m.id}
                  mark={m}
                  metrics={metrics}
                  unit={overlayUnit}
                />
              ))}
              {/* Pegas para ajustar a janela depois de traçada.
                  Cantos, lados e a caixa inteira. Os lados existem
                  porque quase sempre o que se quer é alargar UM lado, e
                  com cantos apenas a caixa acabava sempre torta. Têm
                  captura própria do ponteiro e `pointerEvents` ligado
                  mesmo quando o overlay os tem desligados, para não
                  disputarem o pan nem a selecção. */}
              {current?.view.crop && !toolActive && (
                <>
                  {/* Arrastar a moldura move a janela inteira. É
                      `stroke` e não `all`: o meio da janela tem de
                      continuar a deixar seleccionar elementos. */}
                  <rect
                    x={
                      metrics.toSvg(
                        (cropPreview ?? current.view.crop).minX,
                        (cropPreview ?? current.view.crop).minY,
                      )[0]
                    }
                    y={
                      metrics.toSvg(
                        (cropPreview ?? current.view.crop).minX,
                        (cropPreview ?? current.view.crop).minY,
                      )[1]
                    }
                    width={Math.abs(
                      metrics.toSvg(
                        (cropPreview ?? current.view.crop).maxX,
                        (cropPreview ?? current.view.crop).maxY,
                      )[0] -
                        metrics.toSvg(
                          (cropPreview ?? current.view.crop).minX,
                          (cropPreview ?? current.view.crop).minY,
                        )[0],
                    )}
                    height={Math.abs(
                      metrics.toSvg(
                        (cropPreview ?? current.view.crop).maxX,
                        (cropPreview ?? current.view.crop).maxY,
                      )[1] -
                        metrics.toSvg(
                          (cropPreview ?? current.view.crop).minX,
                          (cropPreview ?? current.view.crop).minY,
                        )[1],
                    )}
                    fill="none"
                    stroke="transparent"
                    strokeWidth={overlayUnit * 10}
                    style={{ pointerEvents: 'stroke', cursor: 'move' }}
                    onPointerDown={(ev) => beginCropDrag(ev, 'move')}
                    onPointerMove={moveCropDrag}
                    onPointerUp={endCropDrag}
                    onPointerCancel={endCropDrag}
                  />
                  {cropHandlePoints(cropPreview ?? current.view.crop).map(
                    (pega) => {
                      const [hx, hy] = metrics.toSvg(pega.x, pega.y)
                      const lado = overlayUnit * 7
                      // Área de agarrar maior do que o quadrado que se
                      // vê: a pega desenhada a 7 unidades era
                      // demasiado pequena para acertar com o rato num
                      // desenho afastado.
                      const agarrar = overlayUnit * 16
                      return (
                        <g key={pega.id}>
                          <rect
                            x={hx - agarrar / 2}
                            y={hy - agarrar / 2}
                            width={agarrar}
                            height={agarrar}
                            fill="transparent"
                            style={{
                              pointerEvents: 'auto',
                              cursor: cropHandleCursor(pega.id),
                            }}
                            onPointerDown={(ev) => beginCropDrag(ev, pega.id)}
                            onPointerMove={moveCropDrag}
                            onPointerUp={endCropDrag}
                            onPointerCancel={endCropDrag}
                          />
                          <rect
                            x={hx - lado / 2}
                            y={hy - lado / 2}
                            width={lado}
                            height={lado}
                            fill="#0ea5e9"
                            stroke="#ffffff"
                            strokeWidth={overlayUnit * 0.8}
                            pointerEvents="none"
                          />
                        </g>
                      )
                    },
                  )}
                </>
              )}
              {/* Janela de recorte já definida. Sem a desenhar, o
                  utilizador via o desenho cortado e não tinha como saber
                  onde estava a janela nem que existia. */}
              {current?.view.crop &&
                (() => {
                  const c = cropPreview ?? current.view.crop
                  const [x1, y1] = metrics.toSvg(c.minX, c.minY)
                  const [x2, y2] = metrics.toSvg(c.maxX, c.maxY)
                  return (
                    <rect
                      x={Math.min(x1, x2)}
                      y={Math.min(y1, y2)}
                      width={Math.abs(x2 - x1)}
                      height={Math.abs(y2 - y1)}
                      fill="none"
                      stroke="#0ea5e9"
                      strokeWidth={overlayUnit * 1.2}
                      strokeDasharray={`${overlayUnit * 8} ${overlayUnit * 5}`}
                      pointerEvents="none"
                    />
                  )
                })()}
              {/* Enquanto se arrasta: linha para o corte, rectângulo
                  para a janela de recorte. */}
              {drawP1 && drawCursor && cropMode && (
                <rect
                  x={Math.min(
                    metrics.toSvg(drawP1[0], drawP1[1])[0],
                    metrics.toSvg(drawCursor[0], drawCursor[1])[0],
                  )}
                  y={Math.min(
                    metrics.toSvg(drawP1[0], drawP1[1])[1],
                    metrics.toSvg(drawCursor[0], drawCursor[1])[1],
                  )}
                  width={Math.abs(
                    metrics.toSvg(drawCursor[0], drawCursor[1])[0] -
                      metrics.toSvg(drawP1[0], drawP1[1])[0],
                  )}
                  height={Math.abs(
                    metrics.toSvg(drawCursor[0], drawCursor[1])[1] -
                      metrics.toSvg(drawP1[0], drawP1[1])[1],
                  )}
                  fill="#0ea5e9"
                  fillOpacity={0.08}
                  stroke="#0ea5e9"
                  strokeWidth={overlayUnit * 1.4}
                  strokeDasharray={`${overlayUnit * 6} ${overlayUnit * 4}`}
                />
              )}
              {drawP1 && drawCursor && !cropMode && (
                <line
                  x1={metrics.toSvg(drawP1[0], drawP1[1])[0]}
                  y1={metrics.toSvg(drawP1[0], drawP1[1])[1]}
                  x2={metrics.toSvg(drawCursor[0], drawCursor[1])[0]}
                  y2={metrics.toSvg(drawCursor[0], drawCursor[1])[1]}
                  stroke="#d97706"
                  strokeWidth={overlayUnit * 1.4}
                  strokeDasharray={`${overlayUnit * 6} ${overlayUnit * 4}`}
                />
              )}
              {dimensions.map((dm) => (
                <DimGlyph
                  key={dm.id}
                  dim={dm}
                  metrics={metrics}
                  unit={overlayUnit}
                  selected={selectedDim === dm.id}
                  interactive={measureMode}
                  onSelect={(e, id) => {
                    e.stopPropagation()
                    setSelectedDim(id)
                  }}
                  onDelete={deleteDimension}
                />
              ))}
              <MeasureGlyph
                points={measurePts}
                cursor={measureCursor}
                closed={false}
                result={measureLive}
                metrics={metrics}
                unit={overlayUnit}
              />
              {(pickMode || elemMode) && (
                <PickGlyph
                  picked={pickHighlight.picked}
                  hover={pickHighlight.hover}
                  region={pickHighlight.region}
                  label={pickHighlight.label}
                  labelAt={pickHighlight.labelAt}
                  metrics={metrics}
                  unit={overlayUnit}
                />
              )}
              {annotations.map((a) => (
                <AnnotGlyph
                  key={a.id}
                  annot={a}
                  metrics={metrics}
                  unit={overlayUnit}
                  selected={selectedAnnot === a.id}
                  hidden={editing?.id === a.id}
                  interactive={annotMode}
                  onDown={onAnnotDown}
                  onEdit={(ann) =>
                    setEditing({
                      id: ann.id,
                      at: [ann.x, ann.y],
                      text: ann.text,
                    })
                  }
                  onDelete={(id) => {
                    setAnnotations((list) => removeAnnotation(list, id))
                    setSelectedAnnot(null)
                  }}
                />
              ))}
              {/* Por cima de tudo: a marca do snap é o que diz onde o
                  clique vai mesmo cair. */}
              <SnapGlyph
                hit={toolActive && snapOn ? snapHit : null}
                metrics={metrics}
                unit={overlayUnit}
              />
            </svg>
          </div>
        </div>
      ) : (
        <div className="flex h-full items-center justify-center p-4 text-center text-[11px] text-[var(--color-text-dim)]">
          {busy
            ? 'A gerar a vista…'
            : mobileLayout
              ? 'Escolhe uma vista na lista acima.'
              : 'Escolhe um piso para a planta, ou cria um corte.'}
        </div>
      )}
      {editing && metrics && (
        // Caixa de texto FORA da camada transformada: se fosse dentro, o
        // `scale` do pan/zoom esticava ou encolhia a caixa (a 1:100
        // enquadrado ficava ilegível). Fica em pixéis de ecrã, no ponto
        // exacto onde se clicou.
        <input
          autoFocus
          type="text"
          value={editing.text}
          data-testid="views2d-annot-input"
          aria-label="Texto da anotação"
          placeholder="Texto da anotação"
          onChange={(e) =>
            setEditing((ed) => (ed ? { ...ed, text: e.target.value } : ed))
          }
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => {
            e.stopPropagation()
            if (e.key === 'Enter') {
              e.preventDefault()
              commitEditing()
            } else if (e.key === 'Escape') {
              e.preventDefault()
              setEditing(null)
            }
          }}
          onBlur={commitEditing}
          style={(() => {
            const [left, top] = drawingPointToContainerPx(
              metrics,
              editing.at[0],
              editing.at[1],
              panzoom.state,
              PX_PER_MM,
            )
            // Um pouco acima da linha de base, para a caixa não tapar o
            // ponto que se acabou de clicar.
            return { left, top: top - 22 }
          })()}
          className="absolute z-10 w-[180px] rounded border border-[var(--color-accent-border)] bg-[var(--color-surface)] px-1 py-0.5 text-[11px] text-[var(--color-text)] shadow-lg outline-none"
        />
      )}
      {svg && (
        <button
          type="button"
          onClick={(e) => {
            // Sem isto o clique subia até ao contentor e limpava a
            // selecção (o botão está por cima do desenho).
            e.stopPropagation()
            panzoom.reset()
          }}
          data-testid="views2d-fit"
          className="absolute right-1 top-1 rounded border border-[var(--color-border)] bg-[var(--color-surface)] px-1.5 py-0.5 text-[9px] text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
          title="Voltar a enquadrar o desenho na janela"
        >
          Enquadrar
        </button>
      )}
    </div>
  )

  // --- telemóvel: experiência reduzida -------------------------------------
  if (mobileLayout) {
    return (
      <div
        data-testid="views2d-surface"
        className="absolute inset-0 z-30 flex min-w-0 flex-col overflow-hidden bg-[var(--color-surface)]"
      >
        <div className="flex shrink-0 items-center gap-1 border-b border-[var(--color-border)] bg-[var(--color-panel)] px-1.5 py-1">
          <SurfaceSwitch compact />
          {modelId ? (
            <select
              value={mobileKey}
              onChange={(e) => onMobilePick(e.target.value)}
              disabled={busy}
              aria-label="Vista 2D"
              data-testid="views2d-mobile-picker"
              className="min-w-0 flex-1 rounded border border-[var(--color-border)] bg-[var(--color-bg)] px-1.5 py-1 text-[11px] text-[var(--color-text)] outline-none"
            >
              <option value="">
                {busy ? 'A gerar…' : 'Escolher vista…'}
              </option>
              {mobileOptions.map((o) => (
                <option key={o.key} value={o.key}>
                  {o.label}
                </option>
              ))}
            </select>
          ) : (
            <span className="min-w-0 flex-1 truncate text-[10px] text-[var(--color-text-muted)]">
              Carrega um IFC.
            </span>
          )}
        </div>
        {error && (
          <div className="shrink-0 border-b border-[var(--color-warn-border)] bg-[var(--color-warn-bg)] p-1.5 text-[10px] text-[var(--color-warn-text)]">
            {error}
          </div>
        )}
        {drawingArea}
        {counts && (
          <div className="shrink-0 truncate px-1.5 py-0.5 font-mono text-[9px] text-[var(--color-text-dim)]">
            {counts.total} polilinhas
            {current?.drawing.stats
              ? ` · ${current.drawing.stats.candidates} elementos`
              : ''}
          </div>
        )}
      </div>
    )
  }

  // --- desktop -------------------------------------------------------------
  return deliver(
    <div data-testid="views2d-surface" className={shellClass}>
      <div className="flex h-9 shrink-0 items-center gap-2 border-b border-[var(--color-border)] bg-[var(--color-panel)] px-2">
        {detached ? (
          <span
            data-testid="views2d-detached-badge"
            className="shrink-0 rounded border border-[var(--color-accent-border)] bg-[var(--color-accent-bg)] px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-[var(--color-accent)]"
            title="Esta janela é o modo 2D da aplicação. A janela principal está no 3D e a selecção é a mesma nas duas."
          >
            ⧉ Janela destacada
          </span>
        ) : (
          <SurfaceSwitch />
        )}
        <span className="text-[11px] font-medium text-[var(--color-text)]">
          Vistas 2D
        </span>
        {viewBadge}
        <span className="flex-1" />
        {current && (
          <button
            type="button"
            onClick={() => void view3d()}
            className="rounded border border-[var(--color-border)] px-2 py-1 text-[11px] text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
            title={
              detached
                ? 'Leva a câmara da janela principal para esta vista e põe lá a caixa de corte na fatia desenhada. O desenho fica onde está.'
                : 'Leva a câmara para a vista, põe a caixa de corte exactamente na fatia desenhada e volta ao modo 3D. A câmara mantém-se perspectiva (o motor não expõe projecção ortográfica nesta montagem).'
            }
          >
            Ver no 3D
          </button>
        )}
        {detached ? (
          <button
            type="button"
            onClick={() => setSurface('2d')}
            data-testid="views2d-attach"
            className="shrink-0 rounded border border-[var(--color-border)] px-2 py-1 text-[11px] text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
            title="Fecha esta janela e volta a pôr o 2D na área central da janela principal, com a mesma vista aberta."
          >
            ↘ Trazer de volta
          </button>
        ) : (
          <button
            type="button"
            onClick={() => setDetached(true)}
            data-testid="views2d-detach"
            className="shrink-0 rounded border border-[var(--color-border)] px-2 py-1 text-[11px] text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
            title="Abre o 2D numa janela do browser à parte, para arrastar para um segundo ecrã. A janela principal volta ao 3D e a selecção continua a ser a mesma nas duas."
          >
            ⧉ Destacar
          </button>
        )}
      </div>

      {!modelId ? (
        <div className="p-3 text-[11px] text-[var(--color-text-muted)]">
          Carrega um IFC para gerar plantas e cortes: separador{' '}
          <strong className="text-[var(--color-text)]">Ficheiro</strong> do
          ribbon, ou volta ao 3D para abrir o exemplo.
        </div>
      ) : (
        <div className="flex min-h-0 min-w-0 flex-1">
          {/* Esquerda: navegador de vistas */}
          <div className="w-[248px] shrink-0 overflow-auto border-r border-[var(--color-border)] p-2">
            <ViewNavigator
              storeys={storeys}
              storeysBusy={storeysBusy}
              busy={busy}
              cutOffset={storeyCutOffset}
              onCreatePlan={createPlan}
              freeElevation={freeElevation}
              setFreeElevation={setFreeElevation}
              freeDepth={freeDepth}
              setFreeDepth={setFreeDepth}
              onCreateFreePlan={createFreePlan}
              geometryRange={
                worldBox ? [worldBox.min[1], worldBox.max[1]] : null
              }
              onCreateFromSectionBox={createFromSectionBox}
              manualName={manualName}
              setManualName={setManualName}
              manualAzimuth={manualAzimuth}
              setManualAzimuth={setManualAzimuth}
              manualDepth={manualDepth}
              setManualDepth={setManualDepth}
              manualOrigin={manualOrigin}
              setManualOrigin={setManualOrigin}
              onCreateManualSection={createManualSection}
              saved={saved}
              currentViewId={current?.view.id ?? null}
              onOpenSaved={openSaved}
              onRenameSaved={renameSaved}
              onDeleteSaved={deleteSaved}
              onDuplicateSaved={duplicateSaved}
            />
          </div>

          {/* Centro: barras + desenho + estatísticas */}
          <div className="flex min-h-0 min-w-0 flex-1 flex-col">
            <div className="flex flex-wrap items-center gap-1 px-2 pt-1.5 text-[11px]">
              <NumberField
                label="Corte (m)"
                value={cutOffset}
                step={0.1}
                onChange={setCutOffset}
                compact
                disabled={!isPlan}
                title="Altura do plano de corte acima da cota do piso. Convenção: 1,20 m. Numa planta livre fica a 0 (a cota escrita já é a do corte)."
              />
              <NumberField
                label="Prof. (m)"
                value={depth}
                step={0.5}
                min={0.01}
                compact
                onChange={setDepth}
                title="Profundidade de projecção: até onde para lá do plano de corte se desenham arestas."
              />
              <button
                type="button"
                onClick={regenerate}
                disabled={!current || busy}
                className="rounded border border-[var(--color-accent-border)] bg-[var(--color-accent-bg)] px-2 py-1 text-[var(--color-accent)] disabled:opacity-40"
              >
                {busy ? 'A gerar…' : 'Regenerar'}
              </button>
              <span className="mx-1 h-4 w-px bg-[var(--color-border)]" />
              <select
                value={String(scale)}
                onChange={(e) =>
                  setScale(
                    e.target.value === 'fit' ? 'fit' : Number(e.target.value),
                  )
                }
                title="Escala do desenho no papel"
                aria-label="Escala"
                className="rounded border border-[var(--color-border)] bg-[var(--color-bg)] px-1.5 py-1 text-[11px] outline-none focus:border-[var(--color-accent)]"
              >
                {SCALE_OPTIONS.map((s) => (
                  <option key={String(s)} value={String(s)}>
                    {scaleLabel(s)}
                  </option>
                ))}
              </select>
              <select
                value={paper}
                onChange={(e) => setPaper(e.target.value as PaperName)}
                title="Formato do papel do PDF"
                aria-label="Papel"
                className="rounded border border-[var(--color-border)] bg-[var(--color-bg)] px-1.5 py-1 text-[11px] outline-none focus:border-[var(--color-accent)]"
              >
                <option value="A4">A4</option>
                <option value="A3">A3</option>
              </select>
              <button
                type="button"
                onClick={() => setLandscape((v) => !v)}
                title="Alternar entre retrato e paisagem"
                className="rounded border border-[var(--color-border)] px-2 py-1 text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
              >
                {landscape ? 'Paisagem' : 'Retrato'}
              </button>
              <span className="mx-1 h-4 w-px bg-[var(--color-border)]" />
              <button
                type="button"
                onClick={exportPdf}
                disabled={!visibleDrawing}
                className="rounded border border-[var(--color-border)] px-2 py-1 text-[var(--color-text-muted)] hover:text-[var(--color-text)] disabled:opacity-40"
                title="Gerar o PDF à escala escolhida, com rodapé (nome, escala, data)."
              >
                PDF
              </button>
              <button
                type="button"
                onClick={exportDxf}
                disabled={!visibleDrawing}
                className="rounded border border-[var(--color-border)] px-2 py-1 text-[var(--color-text-muted)] hover:text-[var(--color-text)] disabled:opacity-40"
                title="DXF R12, model space 1:1 em metros, uma camada por categoria IFC e tipo de traço. Abre em qualquer CAD; para DWG, gravar como DWG no CAD (o DWG é formato proprietário sem biblioteca aberta fiável em JS)."
              >
                DXF
              </button>
              <button
                type="button"
                onClick={exportSvg}
                disabled={!visibleDrawing}
                className="rounded border border-[var(--color-border)] px-2 py-1 text-[var(--color-text-muted)] hover:text-[var(--color-text)] disabled:opacity-40"
                title="SVG vectorial, 1 unidade = 1 mm de papel à escala do desenho."
              >
                SVG
              </button>
              <span className="mx-1 h-4 w-px bg-[var(--color-border)]" />
              <button
                type="button"
                onClick={saveCurrent}
                disabled={!current}
                className="rounded border border-[var(--color-border)] px-2 py-1 text-[var(--color-text-muted)] hover:text-[var(--color-text)] disabled:opacity-40"
                title="Gravar esta vista neste modelo (fica no browser, indexada pelo SHA-256 do IFC)."
              >
                Gravar vista
              </button>
              {current?.view.kind === 'section' && (
                <button
                  type="button"
                  onClick={flipCurrent}
                  disabled={busy}
                  className="rounded border border-[var(--color-border)] px-2 py-1 text-[var(--color-text-muted)] hover:text-[var(--color-text)] disabled:opacity-40"
                  title="Olhar para o outro lado do plano de corte."
                >
                  Inverter direção
                </button>
              )}
              {current && current.drawing.layers.length > 0 && (
                <button
                  type="button"
                  onClick={() => setShowLayers((v) => !v)}
                  className="rounded border border-[var(--color-border)] px-2 py-1 text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
                  title="Ligar/desligar camadas por categoria IFC e escolher as cores de cada uma"
                >
                  Camadas ({current.drawing.layers.length}){' '}
                  {showLayers ? '▴' : '▾'}
                </button>
              )}
              <label
                className="flex cursor-pointer items-center gap-1 rounded border border-[var(--color-border)] px-2 py-1 text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
                title="Preencher (poché) os contornos FECHADOS do corte. Os vãos ficam furos (regra par-ímpar). Aplica-se à pré-visualização, ao SVG e ao PDF; o DXF sai só com contornos (o R12 não tem HATCH)."
              >
                <input
                  type="checkbox"
                  checked={fillCuts}
                  onChange={(e) => setFillCuts(e.target.checked)}
                  data-testid="views2d-fill-toggle"
                  className="accent-[var(--color-accent)]"
                />
                Preencher cortes
              </label>
              <label
                className="flex cursor-pointer items-center gap-1 rounded border border-[var(--color-border)] px-2 py-1 text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
                title={
                  'Peso e contraste do traço de PROJECÇÃO. Automática: numa vista dominada pela projecção (uma rede de MEP em planta, por exemplo, em que nada é cortado) a projecção passa a traço principal; nas restantes fica secundária, como manda o desenho técnico. Aplica-se ao ecrã, ao SVG, ao PDF e ao DXF.'
                }
              >
                Projecção
                <select
                  value={projWeight}
                  onChange={(e) =>
                    setProjWeight(e.target.value as ProjWeightChoice)
                  }
                  data-testid="views2d-proj-weight"
                  aria-label="Peso do traço de projecção"
                  className="rounded border border-[var(--color-border)] bg-[var(--color-bg)] px-1 py-0.5 text-[10px] text-[var(--color-text)] outline-none"
                >
                  {PROJ_WEIGHT_CHOICES.map((c) => (
                    <option key={c} value={c}>
                      {c === 'auto'
                        ? `${PROJ_WEIGHT_LABELS[c]} (${
                            PROJ_WEIGHT_LABELS[weights.applied]
                          })`
                        : PROJ_WEIGHT_LABELS[c]}
                    </option>
                  ))}
                </select>
              </label>
              <label
                className="flex cursor-pointer items-center gap-1 rounded border border-[var(--color-border)] px-2 py-1 text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
                title="Esconder as linhas de projecção tapadas por geometria à frente. Custa uma passagem extra sobre os triângulos (o tempo aparece no rodapé); obriga a gerar a vista outra vez."
              >
                <input
                  type="checkbox"
                  checked={hideHidden}
                  disabled={busy}
                  onChange={(e) => {
                    const on = e.target.checked
                    setHideHidden(on)
                    if (current) {
                      void runGeneration(
                        current.view,
                        annotations,
                        dimensions,
                        { hiddenLines: on },
                      )
                    }
                  }}
                  data-testid="views2d-hlr-toggle"
                  className="accent-[var(--color-accent)]"
                />
                Linhas ocultas
              </label>
            </div>

            {current && (
              <div className="flex flex-wrap items-center gap-1 px-2 py-1 text-[11px]">
                {isPlan && (
                  <>
                    <button
                      type="button"
                      onClick={() => {
                        setTool((t) => (t === 'draw' ? 'none' : 'draw'))
                        setDrawP1(null)
                        setDrawCursor(null)
                      }}
                      data-testid="views2d-draw-toggle"
                      // Inactivo com o texto normal (não esbatido): é a
                      // acção principal desta barra e estava a
                      // desaparecer no meio dos botões secundários.
                      className={`rounded border px-2 py-1 font-medium ${
                        drawMode
                          ? 'border-[var(--color-accent-border)] bg-[var(--color-accent-bg)] text-[var(--color-accent)]'
                          : 'border-[var(--color-border)] text-[var(--color-text)] hover:border-[var(--color-accent-border)] hover:text-[var(--color-accent)]'
                      }`}
                      title="Clica dois pontos sobre a planta para definir o traço de corte. O corte olha para a ESQUERDA do sentido do traço. Cada traço fica gravado como Corte A-A, B-B, … Esc cancela."
                    >
                      {drawMode ? 'A desenhar corte…' : 'Desenhar corte'}
                    </button>
                    <NumberField
                      label="Prof. corte (m)"
                      value={drawDepth}
                      step={0.5}
                      min={0.01}
                      compact
                      onChange={setDrawDepth}
                      title="Profundidade dos cortes desenhados."
                    />
                    <label
                      className="flex cursor-pointer items-center gap-1 text-[10px] text-[var(--color-text-muted)]"
                      title="Desenhar sobre a planta as marcas dos cortes já gravados (traço, setas do lado para onde se olha e etiqueta)."
                    >
                      <input
                        type="checkbox"
                        checked={showMarks}
                        onChange={(e) => setShowMarks(e.target.checked)}
                        className="accent-[var(--color-accent)]"
                      />
                      Marcas dos cortes
                    </label>
                    {drawMode && (
                      <span className="text-[10px] text-[var(--color-accent)]">
                        {drawP1
                          ? 'Clica o 2.º ponto (Esc cancela)'
                          : 'Clica o 1.º ponto do traço'}
                      </span>
                    )}
                    <span className="mx-1 h-4 w-px bg-[var(--color-border)]" />
                  </>
                )}
                {!mobileLayout && current && (
                  <>
                    <button
                      type="button"
                      onClick={() => {
                        setTool((t) => (t === 'crop' ? 'none' : 'crop'))
                        setDrawP1(null)
                        setDrawCursor(null)
                      }}
                      data-testid="views2d-crop-toggle"
                      className={`rounded border px-2 py-1 ${
                        cropMode
                          ? 'border-[var(--color-accent-border)] bg-[var(--color-accent-bg)] text-[var(--color-accent)]'
                          : 'border-[var(--color-border)] text-[var(--color-text-muted)] hover:border-[var(--color-accent-border)] hover:text-[var(--color-accent)]'
                      }`}
                      title="Clica dois cantos para limitar o desenho a uma janela. Afecta também o que sai no PDF e no DXF."
                    >
                      {cropMode ? 'A recortar…' : 'Recortar'}
                    </button>
                    {current.view.crop && !cropMode && (
                      <button
                        type="button"
                        onClick={clearCrop}
                        className="rounded border border-[var(--color-border)] px-2 py-1 text-[var(--color-text-muted)] hover:border-[var(--color-accent-border)] hover:text-[var(--color-accent)]"
                        title="Voltar a mostrar o desenho todo."
                      >
                        Sem recorte
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        setTool((t) => (t === 'align' ? 'none' : 'align'))
                        setDrawP1(null)
                        setDrawCursor(null)
                      }}
                      data-testid="views2d-align-toggle"
                      className={`rounded border px-2 py-1 ${
                        alignMode
                          ? 'border-[var(--color-accent-border)] bg-[var(--color-accent-bg)] text-[var(--color-accent)]'
                          : 'border-[var(--color-border)] text-[var(--color-text-muted)] hover:border-[var(--color-accent-border)] hover:text-[var(--color-accent)]'
                      }`}
                      title="Clica dois pontos sobre uma direcção do desenho (uma fachada, um eixo) e a vista roda para essa direcção ficar horizontal. Roda o desenho, nunca o modelo: a georreferenciação não é tocada."
                    >
                      {alignMode ? 'A alinhar…' : 'Alinhar a…'}
                    </button>
                    <button
                      type="button"
                      onClick={() => rotateCurrent(-90)}
                      className="rounded border border-[var(--color-border)] px-2 py-1 text-[var(--color-text-muted)] hover:border-[var(--color-accent-border)] hover:text-[var(--color-accent)]"
                      title="Rodar a vista 90° no sentido horário."
                    >
                      ↻ 90°
                    </button>
                    <button
                      type="button"
                      onClick={() => rotateCurrent(90)}
                      className="rounded border border-[var(--color-border)] px-2 py-1 text-[var(--color-text-muted)] hover:border-[var(--color-accent-border)] hover:text-[var(--color-accent)]"
                      title="Rodar a vista 90° no sentido anti-horário."
                    >
                      ↺ 90°
                    </button>
                    {(cropMode || alignMode) && (
                      <span className="text-[10px] text-[var(--color-accent)]">
                        {drawP1
                          ? 'Clica o 2.º ponto (Esc cancela)'
                          : cropMode
                            ? 'Clica o 1.º canto da janela'
                            : 'Clica o 1.º ponto da direcção'}
                      </span>
                    )}
                    <span className="mx-1 h-4 w-px bg-[var(--color-border)]" />
                  </>
                )}
                <button
                  type="button"
                  onClick={() => {
                    setTool((t) => (t === 'annot' ? 'none' : 'annot'))
                    setEditing(null)
                    clearMeasure()
                    clearPick()
                    setElemPick(null)
                    setSelectedDim(null)
                  }}
                  data-testid="views2d-annot-toggle"
                  className={`rounded border px-2 py-1 font-medium ${
                    annotMode
                      ? 'border-[var(--color-accent-border)] bg-[var(--color-accent-bg)] text-[var(--color-accent)]'
                      : 'border-[var(--color-border)] text-[var(--color-text)] hover:border-[var(--color-accent-border)] hover:text-[var(--color-accent)]'
                  }`}
                  title="Clica no desenho para pôr uma etiqueta de texto. Enter confirma, Esc cancela. As etiquetas arrastam-se, editam-se com duplo clique e vão nos exports (SVG, PDF e DXF)."
                >
                  {annotMode ? 'A anotar…' : 'Anotar'}
                </button>
                {/* Grupo Medir: três maneiras de o fazer, e a que está
                    ligada tem de se ver de longe. */}
                <span className="text-[10px] uppercase tracking-wider text-[var(--color-text-dim)]">
                  Medir
                </span>
                <button
                  type="button"
                  onClick={() => {
                    setTool((t) => (t === 'measure' ? 'none' : 'measure'))
                    setEditing(null)
                    clearMeasure()
                    clearPick()
                    setElemPick(null)
                    setSelectedDim(null)
                  }}
                  data-testid="views2d-measure-toggle"
                  className={`rounded border px-2 py-1 font-medium ${
                    measureMode
                      ? 'border-[var(--color-accent-border)] bg-[var(--color-accent-bg)] text-[var(--color-accent)]'
                      : 'border-[var(--color-border)] text-[var(--color-text)] hover:border-[var(--color-accent-border)] hover:text-[var(--color-accent)]'
                  }`}
                  title="Clica os vértices sobre o desenho. Enter ou duplo clique TERMINAM a medição e deixam-na desenhada como cota (com o valor); clicar no 1.º ponto fecha o polígono e dá área e perímetro. Esc descarta. As cotas gravam-se com a vista e vão nos exports."
                >
                  {measureMode ? 'A medir por pontos…' : 'Por pontos'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setTool((t) => (t === 'pick' ? 'none' : 'pick'))
                    setEditing(null)
                    clearMeasure()
                    clearPick()
                    setElemPick(null)
                    setSelectedDim(null)
                  }}
                  data-testid="views2d-pick-toggle"
                  className={`rounded border px-2 py-1 font-medium ${
                    pickMode
                      ? 'border-[var(--color-accent-border)] bg-[var(--color-accent-bg)] text-[var(--color-accent)]'
                      : 'border-[var(--color-border)] text-[var(--color-text)] hover:border-[var(--color-accent-border)] hover:text-[var(--color-accent)]'
                  }`}
                  title="Escolher linhas JÁ DESENHADAS, como o Pick Lines de um CAD. Um contorno fechado (um círculo, o contorno de um pilar) dá logo a área; troços abertos vão-se encadeando e fecham a fronteira quando as pontas se encontram. Clicar outra vez num troço escolhido larga-o. Enter ou duplo clique gravam a cota, Esc descarta. Um círculo mede π·r² exacto, não o polígono da tesselação."
                >
                  {pickMode ? 'A escolher linhas…' : 'Escolher linhas'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setTool((t) => (t === 'elem' ? 'none' : 'elem'))
                    setEditing(null)
                    clearMeasure()
                    clearPick()
                    setElemPick(null)
                    setSelectedDim(null)
                  }}
                  data-testid="views2d-elem-toggle"
                  className={`rounded border px-2 py-1 font-medium ${
                    elemMode
                      ? 'border-[var(--color-accent-border)] bg-[var(--color-accent-bg)] text-[var(--color-accent)]'
                      : 'border-[var(--color-border)] text-[var(--color-text)] hover:border-[var(--color-accent-border)] hover:text-[var(--color-accent)]'
                  }`}
                  title="Clica num elemento do desenho (na linha ou na mancha do poché) para medir a área da sua secção cortada. Os furos subtraem, como no preenchimento. Enter grava a cota. O elemento fica também seleccionado, com as propriedades à direita."
                >
                  {elemMode ? 'A medir elemento…' : 'Área do elemento'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setSnapOn((on) => {
                      saveSnapPref(!on)
                      return !on
                    })
                    setSnapHit(null)
                  }}
                  aria-pressed={snapOn}
                  data-testid="views2d-snap-toggle"
                  className={`rounded border px-2 py-1 font-medium ${
                    snapOn
                      ? 'border-[var(--color-accent-border)] bg-[var(--color-accent-bg)] text-[var(--color-accent)]'
                      : 'border-[var(--color-border)] text-[var(--color-text-muted)] hover:border-[var(--color-accent-border)] hover:text-[var(--color-accent)]'
                  }`}
                  title={
                    'Agarrar o cursor à geometria do desenho (10 px), para ' +
                    'medir o elemento e não "quase" o elemento. Quadrado = ' +
                    'vértice, X = intersecção de duas linhas, triângulo = ' +
                    'ponto médio, círculo = centro de círculo ou arco. ' +
                    'Alt carregado desliga o snap enquanto ' +
                    'durar o clique; Shift tranca o ponto na horizontal ou ' +
                    'na vertical do ponto anterior (orto). Aplica-se a ' +
                    'Medir, Anotar e Desenhar corte; a selecção nunca ' +
                    'agarra. A escolha fica guardada.'
                  }
                >
                  Snap
                </button>
                {measureMode && (
                  <>
                    <span
                      data-testid="views2d-measure-readout"
                      className="rounded bg-[var(--color-bg)] px-1.5 py-1 font-mono text-[10px] text-[var(--color-text)]"
                    >
                      {measureLive
                        ? measureSummary(measureLive) +
                          (measureAreaHint
                            ? ` · fechar: ${fmtPt(measureAreaHint.area)} m²`
                            : '')
                        : 'Clica o 1.º ponto'}
                    </span>
                    <button
                      type="button"
                      onClick={() => finishMeasure(false)}
                      disabled={measurePts.length < 2}
                      data-testid="views2d-measure-finish"
                      className="rounded border border-[var(--color-border)] px-2 py-1 text-[var(--color-text-muted)] hover:text-[var(--color-text)] disabled:opacity-40"
                      title="Terminar a medição e deixar a cota desenhada (Enter ou duplo clique)."
                    >
                      Terminar cota
                    </button>
                    <button
                      type="button"
                      onClick={() => finishMeasure(true)}
                      disabled={measurePts.length < 3}
                      data-testid="views2d-measure-close-area"
                      className="rounded border border-[var(--color-border)] px-2 py-1 text-[var(--color-text-muted)] hover:text-[var(--color-text)] disabled:opacity-40"
                      title="Fechar o polígono e deixar a cota de área (A e perímetro no centróide). O mesmo que clicar em cima do 1.º ponto."
                    >
                      Fechar área
                    </button>
                    <button
                      type="button"
                      onClick={clearMeasure}
                      disabled={measurePts.length === 0}
                      className="rounded border border-[var(--color-border)] px-2 py-1 text-[var(--color-text-muted)] hover:text-[var(--color-text)] disabled:opacity-40"
                      title="Descartar a medição em curso (Esc)"
                    >
                      Descartar
                    </button>
                  </>
                )}
                {pickMode && (
                  <>
                    <span
                      data-testid="views2d-pick-readout"
                      className="rounded bg-[var(--color-bg)] px-1.5 py-1 font-mono text-[10px] text-[var(--color-text)]"
                    >
                      {pickReadout}
                    </span>
                    <button
                      type="button"
                      onClick={saveChainDimension}
                      disabled={pickedKeys.length === 0}
                      data-testid="views2d-pick-save"
                      className="rounded border border-[var(--color-border)] px-2 py-1 text-[var(--color-text-muted)] hover:text-[var(--color-text)] disabled:opacity-40"
                      title="Gravar o que está escolhido como cota (Enter ou duplo clique). Fechado dá área e perímetro; aberto fica uma cota encadeada."
                    >
                      Gravar cota
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        setPickedKeys((keys) => keys.slice(0, -1))
                      }
                      disabled={pickedKeys.length === 0}
                      data-testid="views2d-pick-undo"
                      className="rounded border border-[var(--color-border)] px-2 py-1 text-[var(--color-text-muted)] hover:text-[var(--color-text)] disabled:opacity-40"
                      title="Desfazer o último troço escolhido."
                    >
                      Desfazer
                    </button>
                    <button
                      type="button"
                      onClick={clearPick}
                      disabled={pickedKeys.length === 0}
                      className="rounded border border-[var(--color-border)] px-2 py-1 text-[var(--color-text-muted)] hover:text-[var(--color-text)] disabled:opacity-40"
                      title="Descartar a escolha (Esc)"
                    >
                      Descartar
                    </button>
                  </>
                )}
                {elemMode && (
                  <>
                    <span
                      data-testid="views2d-elem-readout"
                      className="rounded bg-[var(--color-bg)] px-1.5 py-1 font-mono text-[10px] text-[var(--color-text)]"
                    >
                      {pickReadout}
                    </span>
                    <button
                      type="button"
                      onClick={saveElementDimension}
                      disabled={!elemMeasureResult || elemMeasureResult.loops === 0}
                      data-testid="views2d-elem-save"
                      className="rounded border border-[var(--color-border)] px-2 py-1 text-[var(--color-text-muted)] hover:text-[var(--color-text)] disabled:opacity-40"
                      title="Gravar a área do elemento como cota (Enter). O contorno e os furos ficam tracejados no desenho e vão nos exports."
                    >
                      Gravar cota
                    </button>
                  </>
                )}
                {dimensions.length > 0 && (
                  <>
                    <span
                      data-testid="views2d-dim-count"
                      className="font-mono text-[10px] text-[var(--color-text-dim)]"
                    >
                      {dimensions.length} cota{dimensions.length === 1 ? '' : 's'}
                    </span>
                    {selectedDim && (
                      <button
                        type="button"
                        onClick={() => deleteDimension(selectedDim)}
                        data-testid="views2d-dim-delete-selected"
                        className="rounded border border-[var(--color-border)] px-2 py-1 text-[var(--color-text-muted)] hover:text-red-400"
                        title="Apagar a cota seleccionada (Delete)"
                      >
                        Apagar cota
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        setDimensions([])
                        setSelectedDim(null)
                      }}
                      data-testid="views2d-dim-clear"
                      className="rounded border border-[var(--color-border)] px-2 py-1 text-[var(--color-text-muted)] hover:text-red-400"
                      title="Apagar todas as cotas desta vista."
                    >
                      Limpar cotas
                    </button>
                  </>
                )}
                {annotations.length > 0 && (
                  <span
                    data-testid="views2d-annot-count"
                    className="font-mono text-[10px] text-[var(--color-text-dim)]"
                  >
                    {annotations.length} anotaç
                    {annotations.length === 1 ? 'ão' : 'ões'}
                  </span>
                )}
                {selectedAnnot && (
                  <button
                    type="button"
                    onClick={() => {
                      setAnnotations((list) =>
                        removeAnnotation(list, selectedAnnot),
                      )
                      setSelectedAnnot(null)
                    }}
                    className="rounded border border-[var(--color-border)] px-2 py-1 text-[var(--color-text-muted)] hover:text-red-400"
                    title="Apagar a anotação seleccionada (Delete)"
                  >
                    Apagar anotação
                  </button>
                )}
              </div>
            )}

            {showLayers && current && (
              <div className="mx-2 mb-1 max-h-[160px] overflow-auto rounded border border-[var(--color-border-subtle)] p-1 text-[10px]">
                <label
                  className="mb-1 flex items-center gap-1.5 rounded px-1 py-0.5 hover:bg-[var(--color-bg)]"
                  title="Desenhar as curvas reconhecidas como arcos verdadeiros. Desligar volta à tesselação do modelo, tal como ela vem."
                >
                  <input
                    type="checkbox"
                    checked={smoothArcs}
                    onChange={(e) => setSmoothArcs(e.target.checked)}
                    data-testid="views2d-smooth-arcs"
                    className="accent-[var(--color-accent)]"
                  />
                  <span className="text-[var(--color-text-muted)]">
                    Arcos suaves
                  </span>
                </label>
                <div className="mb-0.5 flex items-center gap-2 px-1 text-[9px] uppercase tracking-wider text-[var(--color-text-dim)]">
                  <span className="flex-1">Camada</span>
                  <span title="Cor do traço e cor do preenchimento">
                    traço / poché
                  </span>
                </div>
                {current.drawing.layers.map((l) => {
                  const custom = layerStyles[l.name]
                  return (
                    <div
                      key={l.name}
                      className="flex items-center gap-1.5 rounded px-1 py-0.5 hover:bg-[var(--color-bg)]"
                    >
                      <input
                        type="checkbox"
                        checked={!hiddenLayers.has(l.name)}
                        onChange={(e) =>
                          setHiddenLayers((prev) => {
                            const next = new Set(prev)
                            if (e.target.checked) next.delete(l.name)
                            else next.add(l.name)
                            return next
                          })
                        }
                        aria-label={`Mostrar a camada ${l.name}`}
                        className="accent-[var(--color-accent)]"
                      />
                      <span className="min-w-0 flex-1 truncate text-[var(--color-text-muted)]">
                        {l.name}
                      </span>
                      <span className="shrink-0 font-mono text-[9px] text-[var(--color-text-dim)]">
                        {l.cut.length}/{l.projection.length}
                      </span>
                      <input
                        type="color"
                        value={custom?.stroke ?? defaultStroke(l)}
                        onChange={(e) =>
                          setLayerStyles((s) =>
                            setLayerStyle(s, l.name, { stroke: e.target.value }),
                          )
                        }
                        data-testid={`views2d-stroke-${l.name}`}
                        aria-label={`Cor do traço da camada ${l.name}`}
                        title={`Cor do traço de ${l.name} (aplica-se à pré-visualização e aos exports; no DXF vai para o índice ACI mais próximo)`}
                        className="h-4 w-6 shrink-0 cursor-pointer rounded border border-[var(--color-border)] bg-transparent p-0"
                      />
                      <input
                        type="color"
                        value={custom?.fill ?? defaultFill(l)}
                        disabled={!fillCuts}
                        onChange={(e) =>
                          setLayerStyles((s) =>
                            setLayerStyle(s, l.name, { fill: e.target.value }),
                          )
                        }
                        data-testid={`views2d-fill-${l.name}`}
                        aria-label={`Cor do preenchimento da camada ${l.name}`}
                        title={`Cor do preenchimento (poché) de ${l.name}`}
                        className="h-4 w-6 shrink-0 cursor-pointer rounded border border-[var(--color-border)] bg-transparent p-0 disabled:opacity-30"
                      />
                      <button
                        type="button"
                        disabled={!custom}
                        onClick={() =>
                          setLayerStyles((s) => resetLayerStyle(s, l.name))
                        }
                        title={`Repor as cores por omissão de ${l.name}`}
                        className="shrink-0 rounded px-1 text-[9px] text-[var(--color-text-dim)] hover:bg-[var(--color-bg)] hover:text-[var(--color-text)] disabled:opacity-25"
                      >
                        repor
                      </button>
                    </div>
                  )
                })}
                {Object.keys(layerStyles).length > 0 && (
                  <button
                    type="button"
                    onClick={() => setLayerStyles({})}
                    className="mt-0.5 w-full rounded border border-[var(--color-border)] px-1 py-0.5 text-[9px] text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
                    title="Repor as cores por omissão de todas as camadas"
                  >
                    Repor todas as cores
                  </button>
                )}
              </div>
            )}

            {error && (
              <div className="mx-2 mb-1 rounded border border-[var(--color-warn-border)] bg-[var(--color-warn-bg)] p-1.5 text-[10px] text-[var(--color-warn-text)]">
                {error}
              </div>
            )}

            {drawingArea}

            <div className="flex shrink-0 flex-wrap gap-x-3 gap-y-0.5 px-2 py-0.5 font-mono text-[9px] text-[var(--color-text-dim)]">
              {counts && (
                <span data-testid="views2d-counts">
                  {counts.cut} corte · {counts.projection} projecção ·{' '}
                  {counts.total} polilinhas
                </span>
              )}
              {/* Informação, não aviso: uma vista só de projecção é
                  legítima (é o normal numa rede de MEP vista de cima) e
                  não tem de interromper ninguém. Diz-se o que é, e o
                  traço da projecção já se adaptou sozinho. */}
              {counts && counts.cut === 0 && counts.projection > 0 && (
                <span data-testid="views2d-no-cut">
                  sem elementos cortados a esta cota: vista só de projecção
                </span>
              )}
              {current?.drawing.stats && (
                <span>
                  {current.drawing.stats.candidates} elementos ·{' '}
                  {current.drawing.stats.triangles} triângulos
                  {current.drawing.stats.skipped > 0
                    ? ` · ${current.drawing.stats.skipped} ilegíveis`
                    : ''}
                  {current.drawing.stats.truncated ? ' · TRUNCADA' : ''}
                  {current.drawing.stats.hiddenLines
                    ? ` · linhas ocultas ${current.drawing.stats.hiddenLines.ms} ms (célula ${current.drawing.stats.hiddenLines.cellM.toFixed(3)} m)`
                    : ''}
                </span>
              )}
              {layout && (
                <span>
                  {paper} {landscape ? 'paisagem' : 'retrato'} · 1:
                  {layout.scaleDenom}
                  {layout.fitted ? ' (fora das escalas normalizadas)' : ''}
                </span>
              )}
              {layout?.overflow && (
                <span
                  data-testid="views2d-pdf-overflow"
                  className="text-amber-500"
                >
                  {pdfOverflowMessage(layout, paper)}
                </span>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Views2DSurface
